import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { SupplierCreateModal } from "@/components/finance/SupplierCreateModal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { categoryValueForEditDisplay, editAssetStatusForDisplay, hydrateEditFormFromCanonicalSpecs, isRawCategoryIdentifier, shouldShowWattwinAdminTools } from "./productCatalogCanonicalHydration";
import { AlertTriangle, Eye, Pencil, Plus, RefreshCw, Tags, Trash2, Upload, X } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

const MANAGER_ROLES = ["admin", "admin_manager", "project_manager"];
const FAMILIES = [
  ["paneles", "Paneles"],
  ["inversores", "Inversores"],
  ["smart_meter", "Smart meter"],
  ["baterias", "Baterías"],
  ["cargadores_ve", "Cargadores VE"],
  ["estructuras", "Estructuras"],
  ["cable_cc", "Cable CC"],
  ["cable_ca", "Cable CA"],
  ["protecciones_cc", "Protecciones CC"],
  ["protecciones_ca", "Protecciones CA"],
  ["cuadros", "Cuadros"],
  ["canalizacion", "Canalización"],
  ["conectores", "Conectores"],
  ["epis", "EPIs"],
  ["consumibles", "Consumibles"],
  ["herramientas", "Herramientas"],
  ["otros", "Otros"],
] as const;

const CATEGORY_LABELS: Record<string, string> = {
  rechargepoint: "Cargadores VE",
  recharge_point: "Cargadores VE",
  inverter: "Inversores",
  inverterred: "Inversores de red",
  inverter_red: "Inversores de red",
  pvmodule: "Paneles",
  pv_module: "Paneles",
  storage: "Baterías",
  wire: "Cable",
  structure: "Estructuras",
  structurekit: "Estructuras",
  structure_kit: "Estructuras",
  extras_inversor: "Extras inversor",
  // Additional Wattwin category keys found in DB (2026-06-14 audit)
  accessories: "Accesorios",
  monitoring: "Monitorización",
  power_analyser: "Analizador de potencia",
  poweranalyser: "Analizador de potencia",
  isolated_inverter: "Inversor aislado",
  isolatedinverter: "Inversor aislado",
  connectors: "Conectores",
};


type FamilyKey = typeof FAMILIES[number][0];
type SpecField = { key: string; label: string; type: "text" | "number" | "boolean" | "select"; unit?: string; requiredForActive?: boolean; group: string; help?: string; options?: string[]; saveAs?: string[] };

const CANONICAL_SPEC_META: Record<string, { label: string; unit?: string; group: string }> = {
  // Datos eléctricos — paneles
  powerWp: { label: "Potencia", unit: "Wp", group: "Datos eléctricos" },
  vocV: { label: "Voc", unit: "V", group: "Datos eléctricos" },
  iscA: { label: "Isc", unit: "A", group: "Datos eléctricos" },
  vmpV: { label: "Vmp", unit: "V", group: "Datos eléctricos" },
  impA: { label: "Imp", unit: "A", group: "Datos eléctricos" },
  efficiencyPct: { label: "Eficiencia", unit: "%", group: "Datos eléctricos" },
  noct: { label: "NOCT", unit: "°C", group: "Datos eléctricos" },
  temperatureCoeffPmax: { label: "Coef. temp. Pmax", unit: "%/°C", group: "Datos eléctricos" },
  temperatureCoeffVoc: { label: "Coef. temp. Voc", unit: "%/°C", group: "Datos eléctricos" },
  temperatureCoeffIsc: { label: "Coef. temp. Isc", unit: "%/°C", group: "Datos eléctricos" },
  // Datos eléctricos — inversores
  nominalPowerW: { label: "Potencia nominal AC", unit: "W", group: "Datos eléctricos" },
  maxDcPowerW: { label: "Potencia máxima DC", unit: "W", group: "Datos eléctricos" },
  mpptCount: { label: "Nº MPPT", group: "Datos eléctricos" },
  stringsPerMppt: { label: "Strings por MPPT", group: "Datos eléctricos" },
  mpptRangeMinV: { label: "Rango MPPT mín.", unit: "V", group: "Datos eléctricos" },
  mpptRangeMaxV: { label: "Rango MPPT máx.", unit: "V", group: "Datos eléctricos" },
  maxInputVoltageV: { label: "Tensión máx. entrada", unit: "V", group: "Datos eléctricos" },
  maxInputCurrentA: { label: "Corriente máx. entrada", unit: "A", group: "Datos eléctricos" },
  phase: { label: "Fase", group: "Datos eléctricos" },
  phases: { label: "Fases", group: "Datos eléctricos" },
  acOutputVoltageV: { label: "Tensión salida AC", unit: "V", group: "Datos eléctricos" },
  frequencyHz: { label: "Frecuencia", unit: "Hz", group: "Datos eléctricos" },
  // Datos eléctricos — baterías
  nominalCapacityWh: { label: "Capacidad nominal", unit: "Wh", group: "Datos eléctricos" },
  usableCapacityWh: { label: "Capacidad útil", unit: "Wh", group: "Datos eléctricos" },
  voltageV: { label: "Tensión", unit: "V", group: "Datos eléctricos" },
  chargePowerW: { label: "Potencia carga", unit: "W", group: "Datos eléctricos" },
  dischargePowerW: { label: "Potencia descarga", unit: "W", group: "Datos eléctricos" },
  // Datos eléctricos — cargadores VE
  maxCurrentA: { label: "Corriente máxima", unit: "A", group: "Datos eléctricos" },
  minCurrentA: { label: "Corriente mínima", unit: "A", group: "Datos eléctricos" },
  outputVoltageV: { label: "Tensión salida", unit: "V", group: "Datos eléctricos" },
  // Dimensiones y peso
  heightMm: { label: "Alto/espesor", unit: "mm", group: "Dimensiones y peso" },
  widthMm: { label: "Ancho", unit: "mm", group: "Dimensiones y peso" },
  depthMm: { label: "Largo", unit: "mm", group: "Dimensiones y peso" },
  weightKg: { label: "Peso", unit: "kg", group: "Dimensiones y peso" },
  cablePositiveMm: { label: "Cable positivo", unit: "mm", group: "Dimensiones y peso" },
  cableNegativeMm: { label: "Cable negativo", unit: "mm", group: "Dimensiones y peso" },
  // Garantías
  productWarrantyYears: { label: "Garantía producto", unit: "años", group: "Garantías" },
  productionWarrantyYears: { label: "Garantía producción", unit: "años", group: "Garantías" },
  performanceWarrantyYears: { label: "Garantía producción", unit: "años", group: "Garantías" },
  annualDegradationRateFirstYear: { label: "Degradación 1er año", unit: "%", group: "Garantías" },
  annualDegradationRateSubsequentYears: { label: "Degradación años sig.", unit: "%", group: "Garantías" },
  cyclesLifetime: { label: "Ciclos vida útil", group: "Garantías" },
  // Construcción/módulo
  bifacial: { label: "Bifacial", group: "Construcción/módulo" },
  cellTechnology: { label: "Tecnología célula", group: "Construcción/módulo" },
  moduleType: { label: "Tipo módulo", group: "Construcción/módulo" },
  frameType: { label: "Marco", group: "Construcción/módulo" },
  cellCount: { label: "Nº celdas", group: "Construcción/módulo" },
  cellsCount: { label: "Nº celdas", group: "Construcción/módulo" },
  diodeCount: { label: "Nº diodos", group: "Construcción/módulo" },
  diodesCount: { label: "Nº diodos", group: "Construcción/módulo" },
  modulesPerPallet: { label: "Módulos/palet", group: "Construcción/módulo" },
  connector: { label: "Conector", group: "Construcción/módulo" },
  connectorType: { label: "Tipo conector", group: "Construcción/módulo" },
  chargeMode: { label: "Modo de carga", group: "Construcción/módulo" },
  gridType: { label: "Tipo red", group: "Construcción/módulo" },
  outputSocketCount: { label: "Nº salidas", group: "Construcción/módulo" },
  chemistry: { label: "Química", group: "Construcción/módulo" },
  compatibility: { label: "Compatibilidad", group: "Construcción/módulo" },
  manufacturer: { label: "Fabricante", group: "Construcción/módulo" },
  // Certificaciones
  protectionIp: { label: "Protección IP", group: "Certificaciones" },
  ipRating: { label: "Grado IP", group: "Certificaciones" },
  communications: { label: "Comunicaciones", group: "Certificaciones" },
  certifications: { label: "Certificaciones", group: "Certificaciones" },
};

const CANONICAL_SPEC_GROUP_ORDER = [
  "Datos eléctricos",
  "Dimensiones y peso",
  "Garantías",
  "Construcción/módulo",
  "Certificaciones",
  "Datos adicionales de Wattwin",
];

const PRODUCT_FAMILY_SPEC_FORMS: Partial<Record<FamilyKey, SpecField[]>> = {
  paneles: [
    { key: "powerWp", label: "Potencia (Wp)", type: "number", unit: "Wp", requiredForActive: true, group: "Eléctrico", help: "Se guarda en Wp. Ej. 615 para un panel de 615 Wp." },
    { key: "vocV", label: "Voc (V)", type: "number", unit: "V", requiredForActive: true, group: "Eléctrico" },
    { key: "iscA", label: "Isc (A)", type: "number", unit: "A", requiredForActive: true, group: "Eléctrico" },
    { key: "vmpV", label: "Vmp (V)", type: "number", unit: "V", requiredForActive: true, group: "Eléctrico" },
    { key: "impA", label: "Imp (A)", type: "number", unit: "A", requiredForActive: true, group: "Eléctrico" },
    { key: "heightMm", label: "Espesor / alto marco (mm)", type: "number", unit: "mm", requiredForActive: true, group: "Dimensiones", help: "Usar mm. Ej. habitual: 30–35 mm (no 3 si son 3 cm)." },
    { key: "widthMm", label: "Ancho módulo (mm)", type: "number", unit: "mm", requiredForActive: true, group: "Dimensiones" },
    { key: "depthMm", label: "Largo módulo (mm)", type: "number", unit: "mm", requiredForActive: true, group: "Dimensiones" },
    { key: "weightKg", label: "Peso (kg)", type: "number", unit: "kg", requiredForActive: true, group: "Dimensiones" },
    { key: "efficiencyPct", label: "Eficiencia (%)", type: "number", unit: "%", requiredForActive: true, group: "Eléctrico" },
    { key: "moduleType", label: "Tipo/material módulo", type: "text", requiredForActive: true, group: "Construcción" },
    { key: "bifacial", label: "Bifacial", type: "boolean", requiredForActive: true, group: "Construcción" },
    { key: "productWarrantyYears", label: "Garantía producto (años)", type: "number", unit: "años", requiredForActive: true, group: "Garantías" },
    { key: "productionWarrantyYears", label: "Garantía producción (años)", type: "number", unit: "años", requiredForActive: true, group: "Garantías" },
    { key: "annualDegradationRateFirstYear", label: "Degradación 1er año (%)", type: "number", unit: "%", group: "Garantías" },
    { key: "annualDegradationRateSubsequentYears", label: "Degradación años siguientes (%)", type: "number", unit: "%", group: "Garantías" },
    { key: "connector", label: "Conector", type: "text", requiredForActive: true, group: "Cableado" },
    { key: "cablePositiveNegativeMm", label: "Sección cable positivo/negativo (mm)", type: "number", unit: "mm", requiredForActive: true, group: "Cableado", saveAs: ["cablePositiveMm", "cableNegativeMm"] },
    { key: "cellsCount", label: "Células", type: "number", group: "Opcional" },
    { key: "diodesCount", label: "Diodos", type: "number", group: "Opcional" },
  ],
  inversores: [
    { key: "nominalPowerW", label: "Potencia nominal AC (W)", type: "number", unit: "W", requiredForActive: true, group: "Potencia", help: "Ej. 8000 para 8 kW" },
    { key: "maxDcPowerW", label: "Potencia máxima DC (W)", type: "number", unit: "W", requiredForActive: true, group: "Potencia", help: "Ej. 8000 para 8 kW" },
    { key: "mpptCount", label: "Nº MPPT", type: "number", requiredForActive: true, group: "Entrada DC" },
    { key: "stringsPerMppt", label: "Strings por MPPT", type: "number", requiredForActive: true, group: "Entrada DC" },
    { key: "mpptRangeMinV", label: "Rango MPPT mín. (V)", type: "number", unit: "V", requiredForActive: true, group: "Entrada DC" },
    { key: "mpptRangeMaxV", label: "Rango MPPT máx. (V)", type: "number", unit: "V", requiredForActive: true, group: "Entrada DC" },
    { key: "maxInputVoltageV", label: "Tensión máxima entrada (V)", type: "number", unit: "V", requiredForActive: true, group: "Entrada DC" },
    { key: "maxInputCurrentA", label: "Corriente máxima entrada (A)", type: "number", unit: "A", requiredForActive: true, group: "Entrada DC" },
    { key: "phase", label: "Fase", type: "select", options: ["mono", "tri"], requiredForActive: true, group: "Salida AC" },
    { key: "acOutputVoltageV", label: "Tensión salida AC (V)", type: "number", unit: "V", requiredForActive: true, group: "Salida AC" },
    { key: "frequencyHz", label: "Frecuencia (Hz)", type: "number", unit: "Hz", requiredForActive: true, group: "Salida AC" },
    { key: "protectionIp", label: "Protección IP", type: "text", requiredForActive: true, group: "Protección" },
    { key: "communications", label: "Comunicaciones", type: "text", group: "Protección", help: "Pendiente de completar" },
  ],
  baterias: [
    { key: "nominalCapacityWh", label: "Capacidad nominal (Wh)", type: "number", unit: "Wh", requiredForActive: true, group: "Capacidad", help: "Ej. 10000 para 10 kWh. Se guarda en Wh." },
    { key: "usableCapacityWh", label: "Capacidad útil (Wh)", type: "number", unit: "Wh", requiredForActive: true, group: "Capacidad", help: "Ej. 8320 para 8,32 kWh. Se guarda en Wh." },
    { key: "voltageV", label: "Tensión (V)", type: "number", unit: "V", requiredForActive: true, group: "Eléctrico" },
    { key: "chemistry", label: "Química", type: "text", requiredForActive: true, group: "Eléctrico" },
    { key: "chargePowerW", label: "Potencia carga (W)", type: "number", unit: "W", requiredForActive: true, group: "Eléctrico", help: "Se guarda en W." },
    { key: "dischargePowerW", label: "Potencia descarga (W)", type: "number", unit: "W", requiredForActive: true, group: "Eléctrico", help: "Se guarda en W." },
    { key: "heightMm", label: "Alto (mm)", type: "number", unit: "mm", requiredForActive: true, group: "Físico" },
    { key: "widthMm", label: "Ancho (mm)", type: "number", unit: "mm", requiredForActive: true, group: "Físico" },
    { key: "depthMm", label: "Fondo (mm)", type: "number", unit: "mm", requiredForActive: true, group: "Físico" },
    { key: "weightKg", label: "Peso (kg)", type: "number", unit: "kg", requiredForActive: true, group: "Físico" },
    { key: "productWarrantyYears", label: "Garantía producto (años)", type: "number", unit: "años", group: "Vida útil", help: "Pendiente de completar" },
    { key: "protectionIp", label: "Protección IP", type: "text", requiredForActive: true, group: "Vida útil" },
    { key: "cyclesLifetime", label: "Ciclos", type: "number", group: "Vida útil", help: "Pendiente de completar" },
    { key: "compatibility", label: "Compatibilidad", type: "text", group: "Compatibilidad", help: "Pendiente de completar" },
  ],
  cargadores_ve: [
    { key: "nominalPowerW", label: "Potencia nominal (W)", type: "number", unit: "W", requiredForActive: true, group: "Eléctrico", help: "Ej. 80000 para 80 kW" },
    { key: "maxCurrentA", label: "Corriente máxima (A)", type: "number", unit: "A", requiredForActive: true, group: "Eléctrico" },
    { key: "minCurrentA", label: "Corriente mínima (A)", type: "number", unit: "A", requiredForActive: true, group: "Eléctrico" },
    { key: "phases", label: "Fases", type: "number", requiredForActive: true, group: "Eléctrico" },
    { key: "connectorType", label: "Tipo conector", type: "text", requiredForActive: true, group: "Carga" },
    { key: "chargeMode", label: "Modo de carga", type: "text", requiredForActive: true, group: "Carga" },
    { key: "gridType", label: "Tipo red", type: "text", requiredForActive: true, group: "Carga" },
    { key: "outputSocketCount", label: "Nº salidas/tomas", type: "number", requiredForActive: true, group: "Carga" },
    { key: "outputVoltageV", label: "Tensión salida (V)", type: "number", unit: "V", requiredForActive: true, group: "Carga" },
    { key: "protectionIp", label: "Protección IP", type: "text", requiredForActive: true, group: "Protección" },
    { key: "communications", label: "Comunicaciones", type: "text", requiredForActive: true, group: "Protección" },
    { key: "heightMm", label: "Alto (mm)", type: "number", unit: "mm", requiredForActive: true, group: "Físico" },
    { key: "widthMm", label: "Ancho (mm)", type: "number", unit: "mm", requiredForActive: true, group: "Físico" },
    { key: "depthMm", label: "Fondo (mm)", type: "number", unit: "mm", requiredForActive: true, group: "Físico" },
    { key: "weightKg", label: "Peso (kg)", type: "number", unit: "kg", requiredForActive: true, group: "Físico" },
  ],
};

type ProductForm = {
  id?: number;
  sku: string;
  family: typeof FAMILIES[number][0];
  category: string;
  brand: string;
  model: string;
  name: string;
  description: string;
  technicalDescription: string;
  unit: string;
  defaultSupplierId: string;
  purchasePriceCurrent: string;
  salePriceRecommended: string;
  barcode: string;
  active: boolean;
  discontinued: boolean;
  notes: string;
  technicalSpecs: Record<string, any>;
};

const emptyProduct: ProductForm = {
  sku: "",
  family: "otros",
  category: "",
  brand: "",
  model: "",
  name: "",
  description: "",
  technicalDescription: "",
  unit: "ud",
  defaultSupplierId: "none",
  purchasePriceCurrent: "",
  salePriceRecommended: "",
  barcode: "",
  active: true,
  discontinued: false,
  notes: "",
  technicalSpecs: {},
};

function cleanNullable(value: string) {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function normalizeDecimalInput(value: unknown) {
  if (typeof value === "number") return Number.isFinite(value) ? value : value;
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  const normalized = trimmed.replace(',', '.');
  const number = Number(normalized);
  return Number.isFinite(number) ? number : value;
}

function normalizeInverterPhaseForForm(value: unknown) {
  const text = String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  if (["1", "mono", "monofasico", "single_phase", "single phase", "1p"].includes(text)) return "mono";
  if (["3", "tri", "trifasico", "three_phase", "three phase", "3p"].includes(text)) return "tri";
  return value;
}

function formatPhaseValue(value: unknown) {
  const normalized = normalizeInverterPhaseForForm(value);
  if (normalized === "mono") return "Monofásico";
  if (normalized === "tri") return "Trifásico";
  return asText(value);
}

function normalizedManualSpecsForPayload(form: ProductForm) {
  const specs = { ...(form.technicalSpecs || {}) };
  for (const field of specFieldsForFamily(form.family)) {
    const raw = specs[field.key];
    if (field.type === "number" && raw !== undefined && raw !== null && raw !== "") {
      const normalized = normalizeDecimalInput(raw);
      specs[field.key] = normalized;
      for (const target of field.saveAs || []) specs[target] = normalized;
    } else if (field.saveAs?.length) {
      for (const target of field.saveAs) specs[target] = raw;
    }
  }
  if (form.family === "inversores" && specs.phase !== undefined) {
    specs.phase = normalizeInverterPhaseForForm(specs.phase);
  }
  if (form.family === "paneles") {
    delete specs.voc;
    delete specs.isc;
    delete specs.vmp;
    delete specs.imp;
    delete specs.cableSection;
    delete specs.cablePositiveNegativeMm;
  }
  return specs;
}

function productPayload(form: ProductForm) {
  return {
    sku: form.sku,
    family: form.family,
    category: cleanNullable(form.category),
    brand: cleanNullable(form.brand),
    model: cleanNullable(form.model),
    name: form.name,
    description: cleanNullable(form.description),
    technicalDescription: cleanNullable(form.technicalDescription),
    unit: form.unit,
    defaultSupplierId: form.defaultSupplierId === "none" ? null : Number(form.defaultSupplierId),
    purchasePriceCurrent: cleanNullable(String(normalizeDecimalInput(form.purchasePriceCurrent) ?? "")),
    salePriceRecommended: cleanNullable(String(normalizeDecimalInput(form.salePriceRecommended) ?? "")),
    barcode: cleanNullable(form.barcode),
    active: form.active,
    discontinued: form.discontinued,
    notes: cleanNullable(form.notes),
    technicalSpecs: normalizedManualSpecsForPayload(form),
  };
}

function familyLabel(value: string) {
  return FAMILIES.find(([key]) => key === value)?.[1] ?? value;
}

function wattwinSpecGroupLabel(group: string) {
  if (group === "Extras Wattwin") return "Datos adicionales de Wattwin";
  return group;
}

function wattwinActionLabel(action: string) {
  if (action === "create_product") return "Acción prevista: Crear producto";
  if (action === "update_existing") return "Acción prevista: Actualizar";
  if (action === "possible_duplicate") return "Posible duplicado";
  if (action === "manual_review") return "Revisión manual";
  return "Omitir";
}

function wattwinActionVariant(action: string) {
  if (action === "possible_duplicate" || action === "manual_review") return "secondary" as const;
  return "outline" as const;
}

function displayWattwinManufacturer(value: unknown) {
  const text = asText(value);
  return text || "pendiente de revisión";
}


function hasValue(value: unknown) {
  if (value === null || value === undefined || value === "") return false;
  if (typeof value === "string" && !value.trim()) return false;
  if (Array.isArray(value)) return value.some(hasValue);
  if (typeof value === "object") return Object.keys(value as Record<string, unknown>).length > 0;
  return true;
}

function asText(value: unknown): string {
  if (!hasValue(value)) return "";
  if (typeof value === "boolean") return value ? "Sí" : "No";
  if (Array.isArray(value)) return value.filter(hasValue).map(asText).filter(Boolean).join(", ");
  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .filter(([, item]) => hasValue(item))
      .map(([key, item]) => `${key}: ${asText(item)}`)
      .join(", ");
  }
  return String(value).trim();
}

function numericValue(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return null;
  const normalized = value.replace(",", ".").replace(/[^0-9.-]/g, "");
  if (!normalized) return null;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
}

function isConfirmedCatalogPrice(value: unknown) {
  const number = numericValue(value);
  return number !== null && number > 0;
}

function formatCatalogPrice(value: unknown) {
  const number = numericValue(value);
  if (number === null || number <= 0) return "Sin precio";
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(number);
}

function expectedMarginValue(purchase: unknown, sale: unknown) {
  const purchaseNumber = numericValue(purchase);
  const saleNumber = numericValue(sale);
  if (purchaseNumber === null || purchaseNumber <= 0 || saleNumber === null || saleNumber <= 0) return "";
  return String(Number((((saleNumber - purchaseNumber) / saleNumber) * 100).toFixed(1)));
}

function expectedMargin(purchase: unknown, sale: unknown) {
  const margin = expectedMarginValue(purchase, sale);
  return margin === "" ? "—" : `${margin} %`;
}

function formatDecimalForForm(value: number, decimals = 2) {
  return String(Number(value.toFixed(decimals)));
}

function applySalePriceChange(form: ProductForm, salePriceRecommended: string): ProductForm {
  return { ...form, salePriceRecommended };
}

function applyMarginPctChange(form: ProductForm, rawMarginPct: string): ProductForm {
  const margin = numericValue(rawMarginPct);
  const purchase = numericValue(form.purchasePriceCurrent);
  if (margin === null || purchase === null || margin < 0 || margin >= 100) return form;
  return { ...form, salePriceRecommended: formatDecimalForForm(purchase / (1 - margin / 100)) };
}

function applyPurchasePriceChange(form: ProductForm, purchasePriceCurrent: string, lastPriceEdit: "sale" | "margin"): ProductForm {
  const purchase = numericValue(purchasePriceCurrent);
  if (lastPriceEdit === "margin") {
    const margin = numericValue(expectedMarginValue(form.purchasePriceCurrent, form.salePriceRecommended));
    if (purchase !== null && margin !== null && margin >= 0 && margin < 100) {
      return { ...form, purchasePriceCurrent, salePriceRecommended: formatDecimalForForm(purchase / (1 - margin / 100)) };
    }
  }
  return { ...form, purchasePriceCurrent };
}


function specFieldsForFamily(family: string) {
  return PRODUCT_FAMILY_SPEC_FORMS[family as FamilyKey] || [];
}

function groupedSpecFields(family: string) {
  return specFieldsForFamily(family).reduce((acc: Record<string, SpecField[]>, field) => {
    if (!acc[field.group]) acc[field.group] = [];
    acc[field.group].push(field);
    return acc;
  }, {});
}

function missingFieldsForActive(form: ProductForm) {
  const missing: string[] = [];
  if (!hasValue(form.sku)) missing.push("SKU");
  if (!hasValue(form.name)) missing.push("Nombre");
  if (!hasValue(form.family)) missing.push("Familia");
  if (!hasValue(form.unit)) missing.push("Unidad");

  const specs = form.technicalSpecs || {};
  for (const field of specFieldsForFamily(form.family)) {
    if (!field.requiredForActive) continue;
    const values = [specs[field.key], ...(field.saveAs || []).map((target) => specs[target])];
    const hasAnyValue = values.some(hasValue);
    const hasInvalidNumber = field.type === "number" && hasAnyValue && values.some((value) => hasValue(value) && (!Number.isFinite(Number(String(value).replace(",", "."))) || Number(String(value).replace(",", ".")) <= 0));
    if (!hasAnyValue || hasInvalidNumber) missing.push(field.label);
  }
  return missing;
}

function productCompleteness(product: any, assets: any[] = []) {
  const required = specFieldsForFamily(product?.family).filter((field) => field.requiredForActive);
  const specs = product?.technicalSpecs || {};
  const missing = required.filter((field) => !hasValue(specs[field.key]) && !(field.saveAs || []).every((target) => hasValue(specs[target]))).map((field) => field.label);
  const hasImage = !!getProductPrimaryImage(assets);
  const hasTechnicalSheet = cachedDocumentAssetsByPurpose(assets, "technical_sheet").length > 0;
  return { missing, complete: missing.length === 0, hasImage, hasTechnicalSheet };
}

function fileToDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function hasCatalogPricing(product: any) {
  return isConfirmedCatalogPrice(product?.purchasePriceCurrent) || isConfirmedCatalogPrice(product?.salePriceRecommended);
}

function isCachedImageAsset(asset: any) {
  return asset?.status === "cached" && asset?.assetType === "image" && !!asset?.viewUrl;
}

function getProductPrimaryImage(assets: any[] = []) {
  return (
    assets.find((asset) => isCachedImageAsset(asset) && asset.purpose === "main_image") ||
    assets.find((asset) => isCachedImageAsset(asset) && asset.purpose === "gallery_image") ||
    assets.find((asset) => isCachedImageAsset(asset)) ||
    null
  );
}

function cachedAssetsByPurpose(assets: any[] = [], purpose: string) {
  return assets.filter((asset) => (asset?.status === "cached" || asset?.status === "external_url_ready") && asset?.purpose === purpose && !!asset?.viewUrl);
}

function cachedDocumentAssetsByPurpose(assets: any[] = [], purpose: string) {
  return cachedAssetsByPurpose(assets, purpose).filter((asset) => asset.assetType !== "image");
}

function assetDisplayName(asset: any, fallback = "Documento") {
  return asset?.title || asset?.fileName || fallback;
}

function documentButtonLabel(purpose: string, count: number) {
  if (purpose === "technical_sheet") return count > 1 ? "Ver fichas técnicas" : "Ver ficha técnica";
  if (purpose === "catalog") return count > 1 ? "Ver catálogos" : "Ver catálogo";
  if (purpose === "certificate") return count > 1 ? "Ver certificados" : "Ver certificado";
  if (purpose === "manual") return count > 1 ? "Ver manuales" : "Ver manual";
  return count > 1 ? "Ver documentos" : "Ver documento";
}

function assetsByProductId(assets: any[] = []) {
  return assets.reduce((acc: Record<number, any[]>, asset: any) => {
    const productId = Number(asset.productId);
    if (!acc[productId]) acc[productId] = [];
    acc[productId].push(asset);
    return acc;
  }, {});
}

const ASSET_PURPOSE_LABELS: Record<string, string> = {
  main_image: "Imagen destacada",
  gallery_image: "Galería",
  technical_sheet: "Ficha técnica",
  catalog: "Catálogo",
  certificate: "Certificado",
  manual: "Manual",
  video: "Vídeo",
  other: "Otros documentos",
  other_document: "Otros documentos",
};

function assetStatusText(asset?: any) {
  if (!asset) return "Pendiente";
  if (asset.status === "cached") return "Cacheado";
  if (asset.status === "external_url_ready") return "URL externa disponible";
  if (asset.status === "resolved_external_ref") return "Referencia localizada · pendiente content/stream";
  if (asset.status === "content_stream_not_found") return "Pendiente endpoint: content/stream no encontrado";
  if (asset.status === "auth_required_or_forbidden") return "Auth requerida o prohibida";
  if (asset.status === "vendor_content_resource" || asset.status === "vendor_path_forbidden") return "Imagen del fabricante no descargable por API";
  if (asset.status === "pending_resolution") return "Pendiente de URL/binario";
  if (asset.status === "invalid_binary_response") return "Binario inválido";
  if (asset.status === "failed_spaces_write") return "Falló escritura Spaces";
  if (asset.status === "failed_wattwin_download") return "Falló descarga Wattwin";
  if (asset.status === "failed") return "Falló resolución";
  if (asset.status === "skipped") return "Omitido";
  return "Pendiente de resolver";
}

function wattwinAssetStatusText(status?: string) {
  if (status === "completed_cached") return "Cacheado";
  if (status === "completed_partial") return "Parcial";
  if (status === "completed_pending_resolution") return "Pendiente endpoint";
  if (status === "failed_spaces_write") return "Fallo Spaces staging";
  if (status === "failed_wattwin_download") return "Fallo descarga/validación Wattwin";
  return status || "—";
}


const ASSET_QUALITY_FILTERS = {
  all_active: "Activos operativos",
  a_all: "A técnicos principales",
  a_complete: "A completos",
  a_pending_image: "A pendientes de imagen · Deuda real",
  a_pending_datasheet: "A pendientes de ficha · Deuda real",
  b_partial: "B parcial",
  c_not_applicable: "C no aplica",
  d_manual_review: "D revisión manual",
} as const;

type AssetQualityFilter = keyof typeof ASSET_QUALITY_FILTERS;

function matchesAssetQualityFilter(row: any, filter: AssetQualityFilter) {
  if (filter === "all_active") return true;
  if (filter === "a_all") return row?.assetRequirementClass === "A";
  if (filter === "a_complete") return row?.assetRequirementClass === "A" && row?.qualityStatus === "complete";
  if (filter === "a_pending_image") return row?.assetRequirementClass === "A" && row?.imageStatus === "missing";
  if (filter === "a_pending_datasheet") return row?.assetRequirementClass === "A" && row?.datasheetStatus === "missing";
  if (filter === "b_partial") return row?.assetRequirementClass === "B";
  if (filter === "c_not_applicable") return row?.assetRequirementClass === "C";
  if (filter === "d_manual_review") return row?.assetRequirementClass === "D";
  return true;
}

function qualitySeverityVariant(severity: string) {
  if (severity === "critical") return "destructive" as const;
  if (severity === "warning") return "secondary" as const;
  return "outline" as const;
}

function qualitySeverityLabel(severity: string) {
  if (severity === "critical") return "Crítico";
  if (severity === "warning") return "Requiere revisión";
  return "Informativo";
}


function readableCategoryFallback(value: string) {
  return value
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function categoryDisplayLabel(value: unknown) {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  const match = raw.match(/^definitions\.productCategory\.categories\.([^.]+)\.NAME$/i);
  const token = match?.[1] || raw;
  const normalized = token
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .toLowerCase();
  return CATEGORY_LABELS[normalized] || CATEGORY_LABELS[normalized.replace(/_/g, "")] || readableCategoryFallback(token);
}

function productQualityBadges(issues: any[] = []) {
  const codes = new Set(issues.map((issue) => issue.code));
  return [
    codes.has("missing_required_specs") && "Incompleto",
    codes.has("missing_main_image") && "Sin imagen",
    codes.has("missing_technical_sheet") && "Sin ficha",
    Array.from(codes).some((code) => String(code).startsWith("suspicious_")) && "Datos dudosos",
    (codes.has("missing_purchase_price") || codes.has("missing_sale_price")) && "Precio incompleto",
    codes.has("possible_duplicate") && "Duplicado posible",
  ].filter(Boolean) as string[];
}

function cachedByPurpose(summary: any, purposes: string[]) {
  const rows = Array.isArray(summary?.rows) ? summary.rows : [];
  return rows.reduce((sum: number, row: any) => sum + (Array.isArray(row.diagnostics) ? row.diagnostics.filter((diag: any) => purposes.includes(diag.purpose) && diag.cached).length : 0), 0);
}

function pendingByPurpose(summary: any, purposes: string[]) {
  const rows = Array.isArray(summary?.rows) ? summary.rows : [];
  return rows.reduce((sum: number, row: any) => sum + (Array.isArray(row.diagnostics) ? row.diagnostics.filter((diag: any) => purposes.includes(diag.purpose) && !diag.cached).length : 0), 0);
}

function wattwinDeepRows(summary: any) {
  return Array.isArray(summary?.products) ? summary.products : Array.isArray(summary?.rows) ? summary.rows : [];
}

function wattwinDeepSummary(summary: any) {
  const rows = wattwinDeepRows(summary);
  const total = summary?.summary?.total ?? summary?.total ?? rows.length;
  const ok = summary?.summary?.ok ?? rows.filter((row: any) => row?.status === "ok").length;
  const endpointPending = summary?.summary?.endpointPending ?? rows.filter((row: any) => row?.status === "endpoint_pending").length;
  return { total, ok, endpointPending, rows };
}

function wattwinDryRunIsOk(summary: any) {
  const { total, ok, endpointPending, rows } = wattwinDeepSummary(summary);
  return total === 7 && ok === 7 && endpointPending === 0 && rows.length === 7 && rows.every((row: any) => row?.status === "ok");
}

function wattwinDryRunDisabledReason(summary: any) {
  if (!summary) return "Primero ejecuta la auditoría en seco de los 7 productos.";
  const { total, ok, endpointPending } = wattwinDeepSummary(summary);
  if (total !== 7) return `La auditoría debe devolver 7 productos; ahora devuelve ${total}.`;
  if (ok !== 7 || endpointPending > 0) return `La auditoría no está limpia: ${ok}/7 OK, ${endpointPending} pendientes/error.`;
  return "La auditoría aún no está marcada como OK para los 7 productos.";
}

function fieldList(value: any, max = 8) {
  const values = Array.isArray(value) ? value.filter(Boolean).map(String) : [];
  if (!values.length) return "—";
  return values.slice(0, max).join(", ") + (values.length > max ? ` +${values.length - max}` : "");
}

function previewStatusLabel(status: string) {
  if (status === "already_imported") return "Ya existe en App ODS";
  if (status === "new_product") return "Pendiente de traer";
  if (status === "update_existing") return "Cambio detectado";
  if (status === "possible_duplicate") return "Posible duplicado";
  if (status === "manual_review") return "Revisión manual";
  if (status === "skip_low_relevance") return "Baja relevancia";
  return status || "—";
}

function previewStatusVariant(status: string) {
  if (status === "possible_duplicate" || status === "manual_review") return "secondary" as const;
  if (status === "update_existing" || status === "new_product") return "default" as const;
  return "outline" as const;
}

function yesNo(value: unknown) {
  return value ? "Sí" : "No";
}

function csvCell(value: unknown) {
  const text = Array.isArray(value) ? value.join(" | ") : String(value ?? "");
  return `"${text.replace(/"/g, '""')}"`;
}

function wattwinPreviewCsv(rows: any[]) {
  const headers = ["wattwinProductId", "nombre", "fabricante", "modeloRef", "familia", "categoryId", "categoryCode", "existeODS", "localProductId", "confianza", "estado", "imagen", "ficha", "documentos", "razones"];
  const lines = rows.map((row) => [
    row.wattwinProductId,
    row.name,
    row.manufacturer,
    row.modelRef,
    row.familyDetected,
    row.categoryId ?? row.wattwinCategoryId,
    row.categoryCode ?? row.wattwinCategoryCode,
    yesNo(row.existsInOds),
    row.localProductId,
    row.matchConfidence,
    previewStatusLabel(row.suggestedStatus),
    yesNo(row.hasMainImage),
    yesNo(row.hasTechnicalSheet),
    yesNo(row.hasDocumentIds),
    Array.isArray(row.reasons) ? row.reasons.join(" | ") : "",
  ].map(csvCell).join(","));
  return [headers.map(csvCell).join(","), ...lines].join("\n");
}

function downloadTextFile(fileName: string, content: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function proposedCanonicalFields(row: any) {
  return fieldList(row?.canonicalFieldsList || row?.proposedFields || row?.specsComparison?.canonicalKeysPresent || (typeof row?.canonicalFields === "number" ? [`${row.canonicalFields} campos`] : []));
}

function titleCaseToken(value: string) {
  return value
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

type SpecFormat = "text" | "power" | "w" | "kw" | "kwh" | "v" | "a" | "kg" | "mm" | "percent" | "tempPercent" | "years" | "ip" | "connector" | "boolean" | "phase";

function formatSpecValue(value: unknown, format: SpecFormat = "text") {
  if (!hasValue(value)) return "—";
  if (format === "boolean") return value === true || String(value).toLowerCase() === "true" ? "Sí" : value === false || String(value).toLowerCase() === "false" ? "No" : asText(value);
  if (format === "ip") return asText(value).replace(/IP[_\s-]*/i, "IP").replace(/_/g, "").toUpperCase();
  if (format === "phase") return formatPhaseValue(value);
  if (format === "connector") {
    const text = asText(value).toLowerCase().replace(/css/g, "ccs");
    if (text === "ccs") return "CCS";
    if (text.includes("mc4") && text.includes("evo2")) return "MC4 EVO2";
    return text.split(/([,;/])/).map((part: string) => /^[,;/]$/.test(part) ? part : titleCaseToken(part.trim())).join(" ").replace(/\s+([,;/])\s+/g, "$1 ");
  }
  const rendered = asText(value);
  const number = numericValue(value);
  if (format === "power") {
    if (number !== null && number >= 1000 && Number.isInteger(number / 1000)) return `${number / 1000} kW`;
    if (number !== null && number >= 1000) return `${Number((number / 1000).toFixed(2))} kW`;
    return rendered.toLowerCase().includes("w") ? rendered : `${rendered} W`;
  }
  if (format === "w") return rendered.toLowerCase().includes("w") ? rendered : `${rendered} W`;
  if (format === "kw") return rendered.toLowerCase().includes("kw") ? rendered : `${rendered} kW`;
  if (format === "kwh") {
    if (number !== null && number >= 1000) return `${Number((number / 1000).toFixed(2)).toLocaleString("es-ES")} kWh`;
    return rendered.toLowerCase().includes("kwh") || rendered.toLowerCase().includes("kw h") ? rendered : `${rendered} kWh`;
  }
  if (format === "v") return rendered.toLowerCase().includes("v") ? rendered : `${rendered} V`;
  if (format === "a") return rendered.toLowerCase().includes("a") ? rendered : `${rendered} A`;
  if (format === "kg") return rendered.toLowerCase().includes("kg") ? rendered : `${rendered} kg`;
  if (format === "mm") return rendered.toLowerCase().includes("mm") ? rendered : `${rendered} mm`;
  if (format === "percent") return rendered.includes("%") ? rendered : `${rendered} %`;
  if (format === "tempPercent") return rendered.includes("%/°C") || rendered.includes("%/ºC") ? rendered : `${rendered} %/°C`;
  if (format === "years") return rendered.toLowerCase().includes("año") ? rendered : `${rendered} años`;
  return rendered;
}

function normalizeKey(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function rawSpecEntries(specs: Record<string, unknown>) {
  const raw = (specs.rawProperties && typeof specs.rawProperties === "object" ? specs.rawProperties : {}) as Record<string, unknown>;
  return Object.entries(raw).map(([key, value]) => ({ key, normalized: normalizeKey(key), value }));
}

function findSpec(specs: Record<string, unknown>, directKeys: string[], rawKeys: string[] = []) {
  for (const key of directKeys) {
    if (hasValue(specs[key])) return specs[key];
  }
  const wanted = [...directKeys, ...rawKeys].map(normalizeKey);
  const raw = rawSpecEntries(specs);
  const exact = raw.find((entry) => wanted.includes(entry.normalized) && hasValue(entry.value));
  if (exact) return exact.value;
  const partial = raw.find((entry) => wanted.some((key) => entry.normalized.includes(key)) && hasValue(entry.value));
  return partial?.value;
}

const INVERTER_COMMUNICATION_RAW_KEYS = [
  "connectivity",
  "communication",
  "communications",
  "comunicacion",
  "comunicaciones",
  "communication_protocol",
  "communication_protocols",
  "com_interfaces",
  "interfaces",
  "interface",
  "monitoring",
  "wifi",
  "wi_fi",
  "wlan",
  "ethernet",
  "rs485",
  "modbus",
  "app",
  "dongle",
  "datalogger",
];

function inverterCommunicationsValue(specs: Record<string, unknown>) {
  return findSpec(specs, ["communications"], INVERTER_COMMUNICATION_RAW_KEYS);
}

function joinSpecs(parts: unknown[], separator = " × ") {
  const values = parts.filter(hasValue).map(asText).filter(Boolean);
  return values.length ? values.join(separator) : undefined;
}

type SpecItem = { label: string; value: unknown; format?: SpecFormat; mappingPending?: boolean };
type SpecGroup = { title: string; items: SpecItem[] };

function specItem(label: string, value: unknown, format: SpecFormat = "text", mappingPending = false): SpecItem {
  return { label, value, format, mappingPending };
}

function visibleGroups(groups: SpecGroup[]) {
  return groups
    .map((group) => ({ ...group, items: group.items.filter((item) => hasValue(item.value) && !item.mappingPending) }))
    .filter((group) => group.items.length > 0);
}

function dimensionsFromRaw(specs: Record<string, unknown>) {
  return findSpec(specs, ["dimensions"], ["dimensions", "dimension", "dimensiones"])
    || joinSpecs([
      findSpec(specs, ["heightMm"], ["length", "largo", "longitud", "module_height", "height", "alto"]),
      findSpec(specs, ["widthMm"], ["width", "ancho", "module_width"]),
      findSpec(specs, ["depthMm"], ["depth", "profundidad", "thickness", "espesor", "module_thickness"]),
    ]);
}

function mpptRangeFromRaw(specs: Record<string, unknown>) {
  return findSpec(specs, ["mpptRange"], ["mppt_range", "rango_mppt"])
    || joinSpecs([
      findSpec(specs, [], ["MPP_voltage_min", "mppt_voltage_min", "mpp_min"]),
      findSpec(specs, [], ["MPP_voltage_max", "mppt_voltage_max", "mpp_max"]),
    ], " – ");
}

function safeInverterPower(value: unknown) {
  const number = numericValue(value);
  // mapping_pending: Wattwin sample includes low values such as 16/36.4 in inverter power-like fields;
  // those are more likely current/voltage than W and are intentionally hidden from the UI.
  if (number !== null && number > 0 && number < 100) return undefined;
  return value;
}

function normalizedDisplaySpecs(family: string, specs: Record<string, unknown>, product?: any): SpecGroup[] {
  const productInfo: SpecGroup = { title: "Información del producto", items: [
    specItem("Referencia", product?.sku),
    specItem("Nombre", product?.name),
    specItem("Descripción corta", product?.description),
  ]};

  if (family === "paneles") {
    return visibleGroups([
      productInfo,
      { title: "Especificaciones generales", items: [
        specItem("Dimensiones", dimensionsFromRaw(specs), "mm"),
        specItem("Espesor/alto marco", findSpec(specs, ["heightMm"], ["height", "alto", "altura", "frame_thickness"]), "mm"),
        specItem("Ancho", findSpec(specs, ["widthMm"], ["width", "ancho", "module_width"]), "mm"),
        specItem("Largo", findSpec(specs, ["depthMm"], ["length", "depth", "largo", "module_depth"]), "mm"),
        specItem("Peso", findSpec(specs, ["weightKg"], ["weight", "peso"]), "kg"),
        specItem("Eficiencia", findSpec(specs, ["efficiencyPct"], ["efficiency", "eficiencia"]), "percent"),
        specItem("Bifacial", findSpec(specs, ["bifacial"], ["bifacial", "bifaciality"]), "boolean"),
        specItem("Garantía producto", findSpec(specs, ["productWarrantyYears"], ["product_warranty", "warranty"]), "years"),
        specItem("Garantía producción / potencia", findSpec(specs, ["productionWarrantyYears"], ["production_warranty", "power_warranty", "linear_power_warranty"]), "years"),
        specItem("Módulos por palet", findSpec(specs, [], ["modules_per_pallet", "modulos_por_palet", "pallet"])),
        specItem("Células", findSpec(specs, ["cellsCount"], ["cells", "cell_number", "cells_number", "number_of_cells", "celulas"])),
        specItem("Diodos", findSpec(specs, ["diodesCount"], ["diodes", "diodes_number", "diodos"])),
        specItem("Longitud cable positivo", findSpec(specs, ["cablePositiveMm"], ["positive_cable_length", "cable_positive", "cable_positivo"]), "mm"),
        specItem("Longitud cable negativo", findSpec(specs, ["cableNegativeMm"], ["negative_cable_length", "cable_negative", "cable_negativo"]), "mm"),
        specItem("Sección cable", findSpec(specs, ["cableSection"], ["cable_section", "section"]), "text"),
        specItem("Tipo conector", findSpec(specs, ["connector"], ["connector_type", "connector"]), "connector"),
        specItem("Tipo módulo / material", findSpec(specs, ["moduleType"], ["module_type", "material", "frame_material"])),
      ]},
      { title: "Especificaciones técnicas STC", items: [
        specItem("Pmax", findSpec(specs, ["powerWp"], ["modules_power_output", "power_output", "potencia", "pmax"]), "power"),
        specItem("Voc", findSpec(specs, ["vocV", "voc"], ["open_circuit_voltage", "voc"]), "v"),
        specItem("Vmp", findSpec(specs, ["vmpV", "vmp"], ["vmp", "vmpp", "voltageAtMaxPower", "voltage_at_max_power", "maximumPowerVoltage", "maximum_power_voltage", "ratedVoltageVmpp", "rated_voltage_vmpp", "voltage_at_pmax", "max_power_voltage", "v_mpp", "vmp_v", "voltage", "voltaje", "voltaje_operacion", "operatingVoltage", "operationVoltage"]), "v"),
        specItem("Isc", findSpec(specs, ["iscA", "isc"], ["short_circuit_current", "isc"]), "a"),
        specItem("Imp", findSpec(specs, ["impA", "imp"], ["imp", "impp", "currentAtMaxPower", "current_at_max_power", "maximumPowerCurrent", "maximum_power_current", "ratedCurrentImpp", "rated_current_impp", "current_at_pmax", "max_power_current", "i_mpp", "imp_a", "current", "corriente", "corriente_operacion", "operatingCurrent", "operationCurrent"]), "a"),
      ]},
      { title: "Otras especificaciones técnicas", items: [
        specItem("Clase protección", findSpec(specs, [], ["protection_class", "class", "ip"]), "ip"),
        specItem("Coeficiente temperatura Pmax", findSpec(specs, ["powerTemperatureCoefficient"], ["temperature_coefficient_power", "power_coefficient"]), "tempPercent"),
        specItem("Coeficiente temperatura Voc", findSpec(specs, ["vocTemperatureCoefficient"], ["temperature_coefficient_voc", "voc_coefficient"]), "tempPercent"),
        specItem("Coeficiente temperatura Isc", findSpec(specs, ["iscTemperatureCoefficient"], ["temperature_coefficient_isc", "isc_coefficient"]), "tempPercent"),
        specItem("Degradación anual", findSpec(specs, [], ["annual_degradation", "degradacion_anual"]), "percent"),
        specItem("Producción garantizada", findSpec(specs, [], ["guaranteed_production", "produccion_garantizada"]), "percent"),
      ]},
    ]);
  }

  if (family === "cargadores_ve") {
    const outputItems = (prefix: string, includeGenericFallback = false) => {
      const fallback = (keys: string[]) => includeGenericFallback ? keys : [];
      return [
        specItem("Tipo conector", findSpec(specs, includeGenericFallback ? ["connectorType"] : [], [`${prefix}_connector_type`, `${prefix}_connector`, ...fallback(["connector_type", "connector"])]), "connector"),
        specItem("Modo recarga", findSpec(specs, includeGenericFallback ? ["chargeMode"] : [], [`${prefix}_recharge_mode`, `${prefix}_charge_mode`, ...fallback(["recharge_mode", "charge_mode"])])),
        specItem("Tipo red", findSpec(specs, includeGenericFallback ? ["gridType"] : [], [`${prefix}_grid_type`, ...fallback(["grid_type", "network_type", "phases"])])),
        specItem("Intensidad mínima", findSpec(specs, includeGenericFallback ? ["minCurrentA"] : [], [`${prefix}_min_current`, ...fallback(["min_current", "output_min_current"])]), "a"),
        specItem("Intensidad máxima", findSpec(specs, includeGenericFallback ? ["maxCurrentA", "current"] : [], [`${prefix}_max_current`, ...fallback(["max_current", "ac_output_current", "intensity"])]), "a"),
        specItem("Potencia nominal", findSpec(specs, includeGenericFallback ? ["nominalPowerW", "power"] : [], [`${prefix}_nominal_power`, ...fallback(["nominal_power", "ve_power_output", "potencia"])]), "power"),
        specItem("Tensión nominal", findSpec(specs, ["outputVoltageV"], [`${prefix}_nominal_voltage`, ...fallback(["nominal_voltage"])]), "v"),
        specItem("Tensión máxima", findSpec(specs, [], [`${prefix}_max_voltage`, ...fallback(["max_voltage"])]), "v"),
        specItem("Tensión mínima", findSpec(specs, [], [`${prefix}_min_voltage`, ...fallback(["min_voltage"])]), "v"),
      ];
    };
    return visibleGroups([
      productInfo,
      { title: "Especificaciones generales", items: [
        specItem("Dimensiones", dimensionsFromRaw(specs), "mm"),
        specItem("Alto", findSpec(specs, [], ["height", "alto"]), "mm"),
        specItem("Ancho", findSpec(specs, [], ["width", "ancho"]), "mm"),
        specItem("Largo", findSpec(specs, [], ["length", "largo", "depth"]), "mm"),
        specItem("Peso", findSpec(specs, ["weightKg"], ["weight", "peso"]), "kg"),
        specItem("IP", findSpec(specs, ["protectionIp"], ["ip", "protection", "protection_degree", "protection_class"]), "ip"),
        specItem("Número tomas", findSpec(specs, ["outputSocketCount"], ["socket_count", "number_of_sockets", "number_of_outlets", "tomas"])),
      ]},
      { title: "Salida 1", items: outputItems("output_1", true) },
      { title: "Salida 2", items: outputItems("output_2") },
      { title: "Comunicaciones / protecciones", items: [
        specItem("Comunicaciones", findSpec(specs, ["communications"], ["connectivity", "communication", "ocpp"])),
        specItem("Protecciones", findSpec(specs, ["protections"], ["protections", "protection"])),
      ]},
    ]);
  }

  if (family === "inversores") {
    return visibleGroups([
      productInfo,
      { title: "Potencia", items: [
        specItem("Potencia nominal", safeInverterPower(findSpec(specs, ["nominalPowerW", "nominalPower"], ["nominal_power", "rated_power", "inverter_power_output", "maximum_apparent_ac_power", "power"])), "power"),
        specItem("Potencia máxima DC", safeInverterPower(findSpec(specs, ["maxDcPowerW", "maxDcPower"], ["maximum_dc_power", "max_dc_power", "max_pv_input_power"])), "power"),
        specItem("Potencia salida AC", safeInverterPower(findSpec(specs, ["acOutputPowerW", "acOutput"], ["ac_output", "output_power", "rated_ac_power", "inverter_power_output", "maximum_apparent_ac_power"])), "power"),
      ]},
      { title: "Entrada DC / MPPT", items: [
        specItem("Número MPPT", findSpec(specs, ["mpptCount"], ["mppt", "mppt_quantity", "number_of_mppt", "mppt_number"])),
        specItem("Rango MPPT", mpptRangeFromRaw(specs), "v"),
        specItem("Tensión máxima DC", findSpec(specs, ["maxInputVoltageV", "maxDcVoltage"], ["maximum_input_voltage", "max_dc_voltage", "vdc"]), "v"),
        specItem("Intensidad máxima entrada", findSpec(specs, ["maxInputCurrentA", "maxInputCurrent"], ["maximum_input_current", "max_input_current", "maxium_input_current_string"]), "a"),
        specItem("Isc admisible", findSpec(specs, ["maxIsc"], ["maximum_short_circuit_current", "isc_admissible", "max_isc"]), "a"),
      ]},
      { title: "Salida AC", items: [
        specItem("Fases", findSpec(specs, ["phase"], ["phase", "phases", "fases"]), "phase"),
        specItem("Tensión nominal", findSpec(specs, ["acOutputVoltageV"], ["nominal_ac_voltage", "ac_voltage", "grid_voltage"]), "v"),
        specItem("Frecuencia", findSpec(specs, ["frequencyHz"], ["frequency", "frecuencia"])),
      ]},
      { title: "Comunicaciones", items: [specItem("Comunicaciones", inverterCommunicationsValue(specs))] },
      { title: "Protecciones", items: [specItem("Protecciones", findSpec(specs, ["protections"], ["protections", "protection"])), specItem("Clase protección", findSpec(specs, [], ["ip", "protection_class"]), "ip")] },
      { title: "Físico", items: [
        specItem("Dimensiones", dimensionsFromRaw(specs), "mm"),
        specItem("Peso", findSpec(specs, ["weightKg"], ["weight", "peso"]), "kg"),
      ]},
    ]);
  }

  if (family === "baterias") {
    return visibleGroups([
      productInfo,
      { title: "Batería", items: [
        specItem("Capacidad", findSpec(specs, ["nominalCapacityWh", "capacity"], ["nominal_energy", "nominal_capacity", "capacity", "energia_nominal"]), "kwh"),
        specItem("Capacidad útil", findSpec(specs, ["usableCapacityWh", "usableCapacity"], ["usable_capacity", "useful_capacity", "storage_capacity_usable"]), "kwh"),
        specItem("Tensión", findSpec(specs, ["voltageV", "voltage"], ["voltage", "tension", "min_working_voltage_single_phase", "max_working_voltage_single_phase"]), "v"),
        specItem("Química", findSpec(specs, ["chemistry"], ["battery_type", "chemistry", "quimica"])),
        specItem("Potencia carga", findSpec(specs, ["chargePowerW"], ["charge_power", "charging_power", "max_power_load"]), "power"),
        specItem("Potencia descarga", findSpec(specs, ["dischargePowerW"], ["discharge_power", "discharging_power", "max_power_discharge"]), "power"),
        specItem("Ciclos", findSpec(specs, ["cyclesLifetime", "cycles"], ["cycles", "cycle_life"])),
        specItem("Compatibilidad", findSpec(specs, ["compatibility", "compatibilities"], ["compatibilities", "compatible_inverters", "compatibility"])),
        specItem("Dimensiones", dimensionsFromRaw(specs), "mm"),
        specItem("Peso", findSpec(specs, ["weightKg"], ["weight", "peso"]), "kg"),
      ]},
    ]);
  }

  return visibleGroups([{ title: "Especificaciones", items: Object.entries(specs).filter(([key]) => key !== "rawProperties" && key !== "schemaVersion").map(([key, value]) => specItem(key, value)) }]);
}

function rawFieldCount(specs: Record<string, unknown>) {
  const raw = specs.rawProperties && typeof specs.rawProperties === "object" ? specs.rawProperties as Record<string, unknown> : {};
  return Object.keys(raw).length;
}

function isCanonicalSpecRow(row: any) {
  return row?.schemaVersion === "canonical-v1";
}

function positiveNumeric(value: unknown) {
  const number = numericValue(value);
  return number !== null && number > 0 ? number : null;
}

function firstPositiveSpecValue(specs: Record<string, unknown>, directKeys: string[], rawKeys: string[] = directKeys) {
  for (const key of directKeys) {
    const value = positiveNumeric(specs?.[key]);
    if (value !== null) return value;
  }
  const raw = specs?.rawProperties && typeof specs.rawProperties === "object" ? specs.rawProperties as Record<string, unknown> : {};
  for (const key of rawKeys) {
    const direct = positiveNumeric(raw[key]);
    if (direct !== null) return direct;
  }
  const found = findSpec(specs || {}, [], rawKeys);
  return positiveNumeric(found);
}

function repairedCanonicalSpecsForDisplay(row: any, siblingRows: any[] = []) {
  const specs = row?.specs && typeof row.specs === "object" ? { ...row.specs } : {};
  if (!isCanonicalSpecRow(row) || row?.family !== "baterias") return specs;
  const legacySpecs = siblingRows
    .filter((item) => item?.id !== row?.id)
    .map((item) => item?.specs && typeof item.specs === "object" ? item.specs : {})
    .filter((item) => Object.keys(item).length > 0);
  if ((positiveNumeric(specs.nominalCapacityWh) ?? 0) <= 0) {
    const nominal = firstPositiveSpecValue(specs, ["nominalCapacityWh"], ["nominal_energy", "nominal_capacity", "capacity"])
      ?? legacySpecs.map((item) => firstPositiveSpecValue(item, ["nominalCapacityWh", "capacity"], ["nominal_energy", "nominal_capacity", "capacity"])).find((value) => value !== null);
    if (nominal !== null && nominal !== undefined) specs.nominalCapacityWh = nominal;
  }
  if ((positiveNumeric(specs.usableCapacityWh) ?? 0) <= 0) {
    const usable = firstPositiveSpecValue(specs, ["usableCapacityWh"], ["storage_capacity_usable", "usable_capacity", "useful_capacity"])
      ?? legacySpecs.map((item) => firstPositiveSpecValue(item, ["usableCapacityWh", "capacity"], ["storage_capacity_usable", "usable_capacity", "useful_capacity", "capacity"])).find((value) => value !== null);
    if (usable !== null && usable !== undefined) specs.usableCapacityWh = usable;
  }
  // Merge optional battery fields from sibling rows (e.g., values manually entered by user)
  // when the canonical Wattwin row does not include them.
  const OPTIONAL_BATTERY_MERGE_KEYS = ["cyclesLifetime", "productWarrantyYears", "compatibility"] as const;
  for (const key of OPTIONAL_BATTERY_MERGE_KEYS) {
    if (!hasValue(specs[key])) {
      const fromSibling = legacySpecs.map((item) => item[key]).find((v) => hasValue(v));
      if (fromSibling !== undefined) specs[key] = fromSibling;
    }
  }
  return specs;
}

function operationalTechnicalSpecsRows(rows: any[] = []) {
  const canonical = rows.find(isCanonicalSpecRow);
  const primary = canonical || rows.find((item) => item?.source === "manual") || rows[0] || null;
  const legacy = canonical ? rows.filter((item) => item?.id !== canonical.id) : rows.filter((item) => item?.id !== primary?.id);
  return {
    primary: primary ? { ...primary, specs: repairedCanonicalSpecsForDisplay(primary, rows) } : null,
    legacy,
  };
}

function rawDisplayEntries(specs: Record<string, unknown>) {
  return Object.entries(specs || {}).filter(([key, value]) => key !== "rawProperties" && key !== "schemaVersion" && hasValue(value));
}

const CATEGORY_PLACEHOLDERS: Partial<Record<FamilyKey, string>> = {
  paneles: "Ej. Módulo FV",
  inversores: "Ej. Inversor string / híbrido",
  baterias: "Ej. Batería HV / batería LV / kit batería",
  cargadores_ve: "Ej. Cargador DC / cargador AC",
};

export default function ProductCatalog() {
  const { user } = useAuth();
  const canManage = MANAGER_ROLES.includes(user?.role || "");
  const canRunWattwinAdmin = ["admin", "admin_manager"].includes(user?.role || "");
  const isStagingUi = typeof window !== "undefined" && window.location.hostname.toLowerCase().includes("staging");
  const canShowWattwinAdminTools = shouldShowWattwinAdminTools({ canRunWattwinAdmin, isStagingUi });
  const canDeleteProductInStagingUi = canRunWattwinAdmin;
  const utils = trpc.useUtils();
  const [search, setSearch] = useState("");
  const [family, setFamily] = useState("all");
  const [active, setActive] = useState("true");
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetProduct, setDeleteTargetProduct] = useState<any | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [aliasDialogOpen, setAliasDialogOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [detailProduct, setDetailProduct] = useState<any | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [alias, setAlias] = useState("");
  const [form, setForm] = useState<ProductForm>(emptyProduct);
  const [initialFormPayloadJson, setInitialFormPayloadJson] = useState<string | null>(null);
  const [lastPriceEdit, setLastPriceEdit] = useState<"sale" | "margin">("sale");
  const marginPct = useMemo(() => expectedMarginValue(form.purchasePriceCurrent, form.salePriceRecommended), [form.purchasePriceCurrent, form.salePriceRecommended]);
  const missingActiveFields = useMemo(() => missingFieldsForActive(form), [form]);
  const canSaveDraftOrChanges = hasValue(form.sku) && hasValue(form.name);
  const [featuredImageFile, setFeaturedImageFile] = useState<File | null>(null);
  const [technicalSheetFile, setTechnicalSheetFile] = useState<File | null>(null);
  const [productFormError, setProductFormError] = useState<string | null>(null);
  const [wattwinApiKey, setWattwinApiKey] = useState("");
  const [wattwinDryRunSummary, setWattwinDryRunSummary] = useState<any | null>(null);
  const [wattwinCanonicalSummary, setWattwinCanonicalSummary] = useState<any | null>(null);
  const [wattwinAssetsSummary, setWattwinAssetsSummary] = useState<any | null>(null);
  const [wattwinReclassifySummary, setWattwinReclassifySummary] = useState<any | null>(null);
  const [assetQualityFilter, setAssetQualityFilter] = useState<AssetQualityFilter | null>(null);
  const [showWattwinAudit, setShowWattwinAudit] = useState(false);
  const [wattwinPreviewOpen, setWattwinPreviewOpen] = useState(false);
  const [wattwinSelectedIds, setWattwinSelectedIds] = useState<Set<string>>(new Set());
  const [wattwinImportDryRunOpen, setWattwinImportDryRunOpen] = useState(false);
  const [wattwinImportDryRunResult, setWattwinImportDryRunResult] = useState<any>(null);
  const [wattwinImportDryRunError, setWattwinImportDryRunError] = useState<string | null>(null);
  const [wattwinImportDryRunLoading, setWattwinImportDryRunLoading] = useState(false);
  const [wattwinImportRealConfirmOpen, setWattwinImportRealConfirmOpen] = useState(false);
  const [wattwinImportRealLoading, setWattwinImportRealLoading] = useState(false);
  const [wattwinImportRealResult, setWattwinImportRealResult] = useState<any>(null);
  const [wattwinImportRealError, setWattwinImportRealError] = useState<string | null>(null);
  const [wattwinPreviewText, setWattwinPreviewText] = useState("");
  const [wattwinPreviewManufacturer, setWattwinPreviewManufacturer] = useState("");
  const [wattwinPreviewFamily, setWattwinPreviewFamily] = useState("all");
  const [wattwinPreviewStatus, setWattwinPreviewStatus] = useState("all");
  const [wattwinPreviewLimit, setWattwinPreviewLimit] = useState("100");
  const [wattwinPreviewOnlyNotImported, setWattwinPreviewOnlyNotImported] = useState(true);
  const [wattwinPreviewOnlyWithImage, setWattwinPreviewOnlyWithImage] = useState(false);
  const [wattwinPreviewOnlyWithTechnicalSheet, setWattwinPreviewOnlyWithTechnicalSheet] = useState(false);
  const [wattwinPreviewNotice, setWattwinPreviewNotice] = useState<string | null>(null);

  const wattwinPreviewInput = useMemo(() => ({
    text: wattwinPreviewText.trim() || undefined,
    manufacturer: wattwinPreviewManufacturer.trim() || undefined,
    family: wattwinPreviewFamily === "all" ? undefined : wattwinPreviewFamily as any,
    status: wattwinPreviewStatus as any,
    onlyNotImported: wattwinPreviewOnlyNotImported,
    onlyWithImage: wattwinPreviewOnlyWithImage,
    onlyWithTechnicalSheet: wattwinPreviewOnlyWithTechnicalSheet,
    limit: Math.min(200, Math.max(1, Number(wattwinPreviewLimit) || 100)),
    scanLimit: 1000,
  }), [wattwinPreviewText, wattwinPreviewManufacturer, wattwinPreviewFamily, wattwinPreviewStatus, wattwinPreviewOnlyNotImported, wattwinPreviewOnlyWithImage, wattwinPreviewOnlyWithTechnicalSheet, wattwinPreviewLimit]);

  const productsQuery = trpc.productCatalog.list.useQuery({
    search: search || undefined,
    family: family === "all" ? undefined : family as any,
    active: active === "all" ? null : active === "true",
  }, { enabled: canManage });
  const suppliersQuery = trpc.productCatalog.listSuppliers.useQuery({ active: true }, { enabled: canManage });
  const aliasesQuery = trpc.productCatalog.listAliases.useQuery({ productId: selectedProductId || 0 }, { enabled: canManage && !!selectedProductId && aliasDialogOpen });
  const technicalSpecsQuery = trpc.productCatalog.listTechnicalSpecs.useQuery({ productId: detailProduct?.id || 0 }, { enabled: canManage && !!detailProduct?.id && detailDialogOpen });
  const mediaRefsQuery = trpc.productCatalog.listMediaRefs.useQuery({ productId: detailProduct?.id || 0 }, { enabled: canManage && !!detailProduct?.id && detailDialogOpen });
  const productIdsForAssets = useMemo(() => {
    const ids = new Set<number>();
    for (const row of productsQuery.data ?? []) {
      const id = Number((row as any)?.product?.id);
      if (Number.isInteger(id) && id > 0) ids.add(id);
    }
    return Array.from(ids);
  }, [productsQuery.data]);
  const assetsBatchQuery = trpc.productCatalog.listAssetsBatch.useQuery(
    { productIds: productIdsForAssets },
    { enabled: canManage && productIdsForAssets.length > 0 },
  );
  const detailAssetsQuery = trpc.productCatalog.listAssets.useQuery({ productId: detailProduct?.id || 0 }, { enabled: canManage && !!detailProduct?.id && detailDialogOpen });
  const editAssetsQuery = trpc.productCatalog.listAssets.useQuery({ productId: form.id || 0 }, { enabled: canManage && !!form.id && productDialogOpen });
  const deletePreviewQuery = trpc.productCatalog.getDeleteProductPreview.useQuery(
    { id: deleteTargetProduct?.id || 0 },
    { enabled: canDeleteProductInStagingUi && deleteDialogOpen && !!deleteTargetProduct?.id, retry: false },
  );
  const qualityQuery = trpc.productCatalog.getQualitySummary.useQuery(undefined, { enabled: canManage });
  const wattwinImportDryRunMutation = trpc.productCatalog.wattwinImportSelectedDryRun.useMutation();
  const wattwinImportSelectedMutation = trpc.productCatalog.wattwinImportSelected.useMutation();
  const wattwinActivateMutation = trpc.productCatalog.wattwinActivateByWattwinId.useMutation();
  const wattwinRemapSpecsMutation = trpc.productCatalog.wattwinRemapSpecsForProduct.useMutation();
  const wattwinCatalogPreviewQuery = trpc.productCatalog.wattwinCatalogPreview.useQuery(wattwinPreviewInput, {
    enabled: false,
    retry: false,
  });

  const invalidateCatalog = async () => {
    await utils.productCatalog.list.invalidate();
    await utils.productCatalog.listSuppliers.invalidate();
    await utils.productCatalog.getQualitySummary.invalidate();
  };

  const createProduct = trpc.productCatalog.create.useMutation({
    onError: (error) => { setProductFormError(error.message); toast.error(error.message); },
  });
  const updateProduct = trpc.productCatalog.update.useMutation({
    onError: (error) => { setProductFormError(error.message); toast.error(error.message); },
  });
  const uploadManualAsset = trpc.productCatalog.uploadManualAsset.useMutation({
    onError: (error) => toast.error(error.message),
  });
  const setProductActive = trpc.productCatalog.setActive.useMutation({
    onSuccess: invalidateCatalog,
    onError: (error) => toast.error(error.message),
  });
  const deleteProduct = trpc.productCatalog.deleteProduct.useMutation({
    onSuccess: async (result) => {
      await invalidateCatalog();
      await utils.productCatalog.listAssetsBatch.invalidate();
      setDeleteDialogOpen(false);
      setDeleteTargetProduct(null);
      setDeleteConfirmText("");
      toast.success(`Producto eliminado en staging: ${result.product.sku}`);
    },
    onError: (error) => toast.error(error.message),
  });
  const addAlias = trpc.productCatalog.addAlias.useMutation({
    onSuccess: async () => { await utils.productCatalog.listAliases.invalidate(); await utils.productCatalog.list.invalidate(); setAlias(""); toast.success("Alias añadido"); },
    onError: (error) => toast.error(error.message),
  });
  const deleteAlias = trpc.productCatalog.deleteAlias.useMutation({
    onSuccess: async () => { await utils.productCatalog.listAliases.invalidate(); await utils.productCatalog.list.invalidate(); },
    onError: (error) => toast.error(error.message),
  });
  const deepReconcileWattwinSample = trpc.productCatalog.deepReconcileWattwinSample.useMutation({
    onSuccess: (summary) => {
      if (summary?.mode === "dry_run") {
        setWattwinDryRunSummary(summary);
        setWattwinCanonicalSummary(null);
        toast.success("Auditoría Wattwin completada");
      } else {
        setWattwinCanonicalSummary(summary);
        toast.success("Normalización canonical aplicada");
      }
      setWattwinApiKey("");
    },
    onError: (error) => toast.error(error.message),
  });
  const resolveWattwinAssetsSample = trpc.productCatalog.resolveWattwinAssetsSample.useMutation({
    onSuccess: async (summary) => {
      setWattwinAssetsSummary(summary);
      setWattwinApiKey("");
      await utils.productCatalog.listAssetsBatch.invalidate();
      await utils.productCatalog.listAssets.invalidate();
      await utils.productCatalog.getQualitySummary.invalidate();
      toast.success("Resolución de assets Wattwin completada");
    },
    onError: (error) => toast.error(error.message),
  });
  const reclassifyWattwinDocumentAssets = trpc.productCatalog.reclassifyWattwinDocumentAssets.useMutation({
    onSuccess: async (summary) => {
      setWattwinReclassifySummary(summary);
      await utils.productCatalog.listAssetsBatch.invalidate();
      await utils.productCatalog.listAssets.invalidate();
      await utils.productCatalog.getQualitySummary.invalidate();
      toast.success("Documentos Wattwin reclasificados");
    },
    onError: (error) => toast.error(error.message),
  });

  const TOP10_IMPORT_PRODUCTS = [
    { id: "6994180379f50e0c444ab383", family: "inversores", schemaVersion: "inverter-v1" },
    { id: "69941811a93ae40c45cb9fe4", family: "inversores", schemaVersion: "inverter-v1" },
    { id: "6994180da93ae40c45cb9fb9", family: "inversores", schemaVersion: "inverter-v1" },
    { id: "6941748a98c3ff0ab3aff1b8", family: "paneles", schemaVersion: "pv-module-v1" },
    { id: "69789068f2df5a0d3f5fb37b", family: "paneles", schemaVersion: "pv-module-v1" },
    { id: "69941811c7053c0cea6c9471", family: "baterias", schemaVersion: "battery-v1" },
    { id: "6994181184eaaf0ceb7f6826", family: "baterias", schemaVersion: "battery-v1" },
    { id: "68907ed1a8df3a0adebb307f", family: "otros", schemaVersion: "generic-v1" },
    { id: "6944162e282cfd0b22e560d6", family: "otros", schemaVersion: "generic-v1" },
    { id: "69941810a93ae40c45cb9fdb", family: "smart_meter", schemaVersion: "generic-v1" },
  ];

  const TOP10_SKU_LABELS = [
    "GW20KSDT-20 — Inversor red Goodwe",
    "GW8000-ES-C10 — Inversor híbrido Goodwe",
    "GW5000N-EH — Inversor híbrido Goodwe",
    "TSM-NE21 715 — Módulo FV Trina Solar",
    "TSM-450 NEG9R.28 — Módulo FV Trina Solar",
    "GW5.1-BAT-D-G20 — Batería Goodwe ESA",
    "LX U5.4-20 — Batería Goodwe Lynx Home",
    "BackupBox-B0 — Accesorio Huawei",
    "20193642 — Cable Prysmian",
    "SCU3000-S — Unidad comunicación Goodwe",
  ];

  const [importConfirmOpen, setImportConfirmOpen] = useState(false);
  const runWattwinImport = trpc.productCatalog.runWattwinImport.useMutation({
    onSuccess: async (result) => {
      setWattwinApiKey("");
      await utils.productCatalog.list.invalidate();
      await utils.productCatalog.getQualitySummary.invalidate();
      const imported = result?.imported || [];
      const errors = result?.errors || [];
      const requested = (result as any)?.limit ?? imported.length + errors.length;
      if (errors.length > 0) {
        toast.error(`Importados ${imported.length}/${requested}. Fallaron ${errors.length}: ${errors.map((e: any) => e.id).join(", ")}`);
      } else {
        toast.success(`Importados ${imported.length} productos de Wattwin`);
      }
    },
    onError: (error) => toast.error(error.message),
  });

  const resetProductDialog = () => {
    setProductFormError(null);
    setFeaturedImageFile(null);
    setTechnicalSheetFile(null);
    setInitialFormPayloadJson(null);
  };

  const openNewProduct = () => {
    resetProductDialog();
    setLastPriceEdit("sale");
    const nextForm = { ...emptyProduct, active: false };
    setForm(nextForm);
    setInitialFormPayloadJson(JSON.stringify(productPayload(nextForm)));
    setProductDialogOpen(true);
  };

  const openDeleteProduct = (product: any, externalRef?: any) => {
    setDeleteTargetProduct({ ...product, externalRef });
    setDeleteConfirmText("");
    setDeleteDialogOpen(true);
  };

  const buildEditProductForm = (product: any, specs: Record<string, any> = {}) => {
    const editSpecs = { ...(specs || {}) };
    if (product?.family === "paneles" && editSpecs.cablePositiveNegativeMm === undefined && editSpecs.cablePositiveMm !== undefined && editSpecs.cablePositiveMm === editSpecs.cableNegativeMm) editSpecs.cablePositiveNegativeMm = editSpecs.cablePositiveMm;
    if (product?.family === "inversores" && editSpecs.phase !== undefined) editSpecs.phase = normalizeInverterPhaseForForm(editSpecs.phase);
    if (product?.family === "inversores" && !hasValue(editSpecs.communications)) {
      const communications = inverterCommunicationsValue(editSpecs);
      if (hasValue(communications)) editSpecs.communications = communications;
    }
    return {
      ...emptyProduct,
      ...product,
      id: product?.id,
      sku: product?.sku ?? "",
      family: product?.family ?? "otros",
      category: product?.category ?? "",
      brand: product?.brand ?? "",
      model: product?.model ?? "",
      name: product?.name ?? "",
      description: product?.description ?? "",
      technicalDescription: product?.technicalDescription ?? "",
      unit: product?.unit ?? "ud",
      technicalSpecs: editSpecs,
      defaultSupplierId: product?.defaultSupplierId ? String(product.defaultSupplierId) : "none",
      purchasePriceCurrent: product?.purchasePriceCurrent ?? "",
      salePriceRecommended: product?.salePriceRecommended ?? "",
      barcode: product?.barcode ?? "",
      notes: product?.notes ?? "",
      active: !!product?.active,
      discontinued: !!product?.discontinued,
    };
  };

  const openEditProduct = (product: any) => {
    if (!product?.id) {
      toast.error("No se pudo abrir la edición: producto inválido");
      return;
    }
    resetProductDialog();
    setLastPriceEdit("sale");

    const initialForm = buildEditProductForm(product, product.technicalSpecs || {});
    setForm(initialForm);
    setInitialFormPayloadJson(JSON.stringify(productPayload(initialForm)));
    setProductDialogOpen(true);

    void (async () => {
      try {
        const specsRows = await utils.productCatalog.listTechnicalSpecs.fetch({ productId: product.id });
        const manualSpecs = (specsRows as any[]).find((item) => item?.source === "manual")?.specs || {};
        const hydratedSpecs = hydrateEditFormFromCanonicalSpecs({
          product,
          existingSpecs: { ...(product.technicalSpecs || {}), ...manualSpecs },
          technicalSpecRows: specsRows as any[],
        });
        const nextForm = buildEditProductForm(product, hydratedSpecs);
        const initialPayload = JSON.stringify(productPayload(initialForm));
        const nextPayload = JSON.stringify(productPayload(nextForm));
        setForm((currentForm) => {
          if (currentForm.id !== product.id) return currentForm;
          return JSON.stringify(productPayload(currentForm)) === initialPayload ? nextForm : currentForm;
        });
        setInitialFormPayloadJson((currentPayload) => currentPayload === initialPayload ? nextPayload : currentPayload);
      } catch (error: any) {
        toast.error(error?.message || "No se pudieron cargar las especificaciones técnicas del producto");
      }
    })();
  };

  const openEditProductById = async (productId: number) => {
    const existing = productById.get(productId);
    if (existing) return openEditProduct(existing);
    const row: any = await utils.productCatalog.getById.fetch({ id: productId });
    return openEditProduct(row.product);
  };

  const uploadSelectedAssets = async (productId: number) => {
    if (featuredImageFile) {
      await uploadManualAsset.mutateAsync({ productId, purpose: "main_image", fileName: featuredImageFile.name, mimeType: featuredImageFile.type, base64: await fileToDataUrl(featuredImageFile) });
    }
    if (technicalSheetFile) {
      await uploadManualAsset.mutateAsync({ productId, purpose: "technical_sheet", fileName: technicalSheetFile.name, mimeType: technicalSheetFile.type, base64: await fileToDataUrl(technicalSheetFile) });
    }
  };

  const saveProductForm = async (activate: boolean) => {
    setProductFormError(null);
    const purchase = numericValue(form.purchasePriceCurrent);
    const sale = numericValue(form.salePriceRecommended);
    if (purchase !== null && sale !== null) {
      const margin = ((sale - purchase) / sale) * 100;
      if (sale <= 0 || margin < 0 || margin >= 100) {
        setProductFormError("El margen previsto debe ser ≥ 0 y < 100; la venta debe ser mayor o igual que la compra si hay margen positivo.");
        return;
      }
    }
    if (!canSaveDraftOrChanges) {
      const message = "Para guardar como borrador/cambios indica al menos SKU y nombre.";
      setProductFormError(message);
      toast.error(message);
      return;
    }
    if (activate && missingActiveFields.length) {
      const message = `Para guardar y activar faltan: ${missingActiveFields.join(", ")}. Puedes guardar como borrador/cambios mientras completas la ficha.`;
      setProductFormError(message);
      toast.error(message);
      return;
    }
    const hasSelectedAssets = !!featuredImageFile || !!technicalSheetFile;
    const currentPayloadJson = JSON.stringify(productPayload(form));
    const formChanged = currentPayloadJson !== initialFormPayloadJson;
    const assetsOnlyEdit = !!form.id && hasSelectedAssets && !formChanged && !activate;
    const nextActive = activate ? true : form.id ? (missingActiveFields.length ? false : form.active) : false;
    const payload = { ...productPayload({ ...form, active: nextActive }), active: nextActive };
    try {
      const product: any = assetsOnlyEdit ? { id: form.id } : form.id ? await updateProduct.mutateAsync({ id: form.id, ...payload }) : await createProduct.mutateAsync(payload);
      await uploadSelectedAssets(product.id);
      await invalidateCatalog();
      await utils.productCatalog.listAssetsBatch.invalidate();
      await utils.productCatalog.listAssets.invalidate();
      await utils.productCatalog.getById.invalidate({ id: product.id });
      await utils.productCatalog.getQualitySummary.invalidate();
      setProductDialogOpen(false);
      setFeaturedImageFile(null);
      setTechnicalSheetFile(null);
      setInitialFormPayloadJson(JSON.stringify(productPayload({ ...form, active: nextActive })));
      toast.success(activate ? "Producto guardado y activado" : form.id ? "Cambios guardados" : "Producto guardado como borrador/incompleto");
    } catch (error: any) {
      const message = error?.message || "No se pudo guardar el producto";
      setProductFormError(message);
      toast.error(message);
    }
  };

  if (!canManage) {
    return (
      <DashboardLayout>
        <Card>
          <CardHeader><CardTitle>Catálogo</CardTitle></CardHeader>
          <CardContent>No tienes permiso para gestionar el catálogo de productos.</CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  const rows = productsQuery.data ?? [];
  const suppliers = suppliersQuery.data ?? [];
  const assetsByProduct = assetsByProductId(assetsBatchQuery.data ?? []);
  const qualitySummary = qualityQuery.data?.summary;
  const assetQuality = qualitySummary?.assetQuality;
  const assetQualityRows = Array.isArray(qualityQuery.data?.assetQualityRows) ? qualityQuery.data.assetQualityRows : [];
  const assetQualityByProductId = new Map(assetQualityRows.map((row: any) => [Number(row.productId), row]));
  const hasAssetQualityRows = assetQualityRows.length > 0;
  const selectedAssetQualityFilter = assetQualityFilter && hasAssetQualityRows ? assetQualityFilter : null;
  const activeAssetQualityFilterLabel = selectedAssetQualityFilter ? ASSET_QUALITY_FILTERS[selectedAssetQualityFilter] : null;
  const selectAssetQualityFilter = (filter: AssetQualityFilter | null) => {
    setAssetQualityFilter(filter === "all_active" ? null : filter);
  };
  const displayRows = selectedAssetQualityFilter
    ? rows.filter((row: any) => matchesAssetQualityFilter(assetQualityByProductId.get(Number(row.product.id)), selectedAssetQualityFilter))
    : rows;
  const activeAssetQualityFilterText = activeAssetQualityFilterLabel ? `${activeAssetQualityFilterLabel} · ${displayRows.length} productos` : null;
  const qualityCardClass = (filter: AssetQualityFilter | null) => [
    "rounded-lg border p-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    hasAssetQualityRows ? "cursor-pointer hover:border-primary/60 hover:bg-muted/50" : "cursor-not-allowed opacity-60",
    (filter === null ? !selectedAssetQualityFilter : selectedAssetQualityFilter === filter) ? "border-primary bg-primary/10 ring-1 ring-primary/30" : "bg-background",
  ].join(" ");
  const qualityMetricButtonClass = (filter: AssetQualityFilter) => [
    "rounded border px-2 py-1 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
    hasAssetQualityRows ? "cursor-pointer hover:border-primary/60 hover:bg-background hover:text-foreground" : "cursor-not-allowed opacity-60",
    selectedAssetQualityFilter === filter ? "border-primary bg-primary/10 text-foreground" : "border-transparent text-muted-foreground",
  ].join(" ");
  const qualityIssues = qualityQuery.data?.issues ?? [];
  const qualityIssuesByProduct = qualityIssues.reduce((acc: Record<number, any[]>, issue: any) => {
    if (!acc[issue.productId]) acc[issue.productId] = [];
    acc[issue.productId].push(issue);
    return acc;
  }, {});
  const filteredQualityIssues = qualityIssues.filter((issue: any) => {
    const assetRow = assetQualityByProductId.get(Number(issue.productId));
    if (selectedAssetQualityFilter && !matchesAssetQualityFilter(assetRow, selectedAssetQualityFilter)) return false;
    return true;
  });
  const productById = new Map(rows.map((row: any) => [row.product.id, row.product]));
  const wattwinDryRunOk = wattwinDryRunIsOk(wattwinDryRunSummary);
  const wattwinDryRunReason = wattwinDryRunDisabledReason(wattwinDryRunSummary);
  const wattwinToolBusy = deepReconcileWattwinSample.isPending || resolveWattwinAssetsSample.isPending || reclassifyWattwinDocumentAssets.isPending || runWattwinImport.isPending || wattwinCatalogPreviewQuery.isFetching;
  const wattwinPreviewRows = wattwinCatalogPreviewQuery.data?.rows ?? [];
  const wattwinPreviewSummary = wattwinCatalogPreviewQuery.data?.summary;
  const wattwinGlobalSummary = (wattwinCatalogPreviewQuery.data as any)?.globalSummary;
  const wattwinSelectedCount = wattwinSelectedIds.size;
  const allVisibleIds = wattwinPreviewRows.map((row: any) => row.wattwinProductId).filter(Boolean) as string[];
  const allVisibleSelected = allVisibleIds.length > 0 && allVisibleIds.every((id) => wattwinSelectedIds.has(id));
  const toggleWattwinSelection = (id: string) => {
    setWattwinSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = wattwinPreviewRows.map((row: any) => row.wattwinProductId).filter(Boolean);
    const allSelected = visibleIds.every((id: string) => wattwinSelectedIds.has(id));
    setWattwinSelectedIds(allSelected ? new Set() : new Set(visibleIds));
  };

  const clearWattwinSelection = () => setWattwinSelectedIds(new Set());

  const closeWattwinPreview = () => {
    setWattwinPreviewOpen(false);
    clearWattwinSelection();
  };

  const runWattwinImportDryRun = async () => {
    const ids = Array.from(wattwinSelectedIds).filter(Boolean);
    if (ids.length === 0) {
      setWattwinImportDryRunError('Selecciona al menos un producto para previsualizar la importación.');
      setWattwinImportDryRunOpen(true);
      return;
    }
    setWattwinImportDryRunLoading(true);
    setWattwinImportDryRunError(null);
    setWattwinImportDryRunResult(null);
    setWattwinImportDryRunOpen(true);
    try {
      const selectedIds = ids.slice(0, 20);
      const manufacturerHints: Record<string, string> = {};
      for (const row of wattwinPreviewRows) {
        if (row.manufacturer && selectedIds.includes(row.wattwinProductId)) {
          manufacturerHints[row.wattwinProductId] = row.manufacturer;
        }
      }
      const result = await wattwinImportDryRunMutation.mutateAsync({ wattwinProductIds: selectedIds, manufacturerHints });
      setWattwinImportDryRunResult(result);
    } catch (e: any) {
      const msg = e?.message ?? 'Error al previsualizar la importación.';
      setWattwinImportDryRunError(
        msg.includes('MISSING_STAGING_SECRET')
          ? 'No se pudo previsualizar: falta configuración del conector Wattwin en staging. No se ha modificado ningún dato.'
          : msg.includes('FORBIDDEN')
          ? 'No tienes permiso para previsualizar importaciones.'
          : 'No se pudo previsualizar la importación. No se ha modificado ningún dato.'
      );
    } finally {
      setWattwinImportDryRunLoading(false);
    }
  };

  const runWattwinImportReal = async () => {
    const ids = Array.from(wattwinSelectedIds).filter(Boolean);
    if (ids.length === 0) {
      const message = 'BLOCKED_WATTWIN_IMPORT_EMPTY_SELECTION';
      setWattwinImportRealError(message);
      toast.error(message);
      return;
    }
    setWattwinImportRealLoading(true);
    setWattwinImportRealError(null);
    setWattwinImportRealResult(null);
    try {
      const manufacturerHints: Record<string, string> = {};
      for (const row of wattwinPreviewRows) {
        if (row.manufacturer && ids.includes(row.wattwinProductId)) {
          manufacturerHints[row.wattwinProductId] = row.manufacturer;
        }
      }
      const result = await wattwinImportSelectedMutation.mutateAsync({
        wattwinProductIds: ids,
        manufacturerHints,
        confirm: 'IMPORT_ONE_WATTWIN_PRODUCT_STAGING_ONLY',
      });
      setWattwinImportRealResult(result);
      if (result.result === 'created' || result.result === 'batch_created') {
        const count = result.result === 'batch_created' ? result.count : 1;
        toast.success(`${count} producto(s) importado(s) correctamente en staging`);
        setWattwinImportRealConfirmOpen(false);
        await invalidateCatalog();
        await utils.productCatalog.wattwinCatalogPreview.invalidate();
      }
    } catch (e: any) {
      const msg = e?.message ?? 'Error al importar.';
      const visibleMessage =
        msg.includes('ENV_VAR_MISSING')
          ? 'BLOCKED_WATTWIN_IMPORT_STAGING_ENV_VAR_MISSING'
          : msg.includes('NOT_STAGING') || msg.includes('PRODUCTION_FORBIDDEN') || (msg.includes('FORBIDDEN') && msg.includes('STAGING'))
          ? 'BLOCKED_WATTWIN_IMPORT_NOT_STAGING_ENV'
          : msg.includes('ROLE_FORBIDDEN')
          ? 'WATTWIN_IMPORT_ROLE_FORBIDDEN'
          : msg.includes('FORBIDDEN') && !msg.includes('BLOCKED_WATTWIN_IMPORT')
          ? 'WATTWIN_IMPORT_FORBIDDEN'
          : msg.includes('already_imported')
          ? 'BLOCKED_WATTWIN_IMPORT_ALREADY_IMPORTED'
          : msg;
      setWattwinImportRealError(visibleMessage);
      toast.error(visibleMessage);
    } finally {
      setWattwinImportRealLoading(false);
    }
  };

  const runWattwinCatalogPreview = async () => {
    setWattwinPreviewNotice(null);
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (!settled) setWattwinPreviewNotice("Tiempo de espera agotado al consultar Wattwin. Preview only: no se ha importado ni modificado nada.");
    }, 25000);
    const result = await wattwinCatalogPreviewQuery.refetch();
    settled = true;
    window.clearTimeout(timeout);
    if (result.error) {
      setWattwinPreviewNotice("No se pudo consultar Wattwin en staging. Revisa la configuración del conector. No se ha modificado ningún dato.");
      return;
    }
    setWattwinPreviewNotice(null);
    toast.success("Previsualización Wattwin actualizada");
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold">Catálogo</h1>
            <p className="text-muted-foreground">Productos, alias y proveedor principal. P1 independiente de Materiales.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setSupplierDialogOpen(true)}>Nuevo proveedor</Button>
            <Button onClick={openNewProduct}><Plus className="mr-2 h-4 w-4" />Nuevo producto</Button>
          </div>
        </div>

        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5" />Calidad catálogo operativo</CardTitle>
                  <CardDescription>Valida si el producto puede usarse con seguridad en cálculos/presupuestos.</CardDescription>
                </div>
                {canManage && (
                  <div className="flex flex-col gap-2 sm:flex-row sm:shrink-0">
                    <Button size="sm" variant="default" className="w-full sm:w-auto" title="Preview only: no importa ni escribe en base de datos" onClick={() => setWattwinPreviewOpen(true)}>
                      <RefreshCw className="mr-2 h-4 w-4" />Sincronizar catálogo Wattwin ODS
                    </Button>
                    <Button size="sm" variant="outline" className="w-full sm:w-auto" onClick={() => setShowWattwinAudit(!showWattwinAudit)}>
                      {showWattwinAudit ? "Ocultar" : "Mostrar"} auditoría Wattwin
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {qualityQuery.isLoading ? <div className="text-sm text-muted-foreground">Calculando…</div> : (
                <>
                  {assetQuality ? (
                    <>
                      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                        <button
                          type="button"
                          className={qualityCardClass(null)}
                          disabled={!hasAssetQualityRows}
                          onClick={() => selectAssetQualityFilter(null)}
                        >
                          <div className="text-xs text-muted-foreground">Activos operativos</div>
                          <div className="text-2xl font-semibold">{assetQuality.totalActive ?? 0}</div>
                          <div className="text-xs text-muted-foreground">Wattwin: {qualitySummary?.origin?.wattwin ?? 0} · Manual: {qualitySummary?.origin?.manual ?? 0}</div>
                        </button>
                        <div className={qualityCardClass("a_all")} role="group" aria-label="Filtros A técnicos principales">
                          <button
                            type="button"
                            className="w-full rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed"
                            disabled={!hasAssetQualityRows}
                            onClick={() => selectAssetQualityFilter("a_all")}
                          >
                            <div className="text-xs text-muted-foreground">A — Técnicos principales</div>
                            <div className="text-2xl font-semibold">{assetQuality.classA?.total ?? 0}</div>
                            <div className="text-xs text-muted-foreground">Todos los técnicos principales</div>
                          </button>
                          <div className="mt-2 grid grid-cols-1 gap-1 text-xs">
                            <button type="button" className={qualityMetricButtonClass("a_complete")} disabled={!hasAssetQualityRows} onClick={() => selectAssetQualityFilter("a_complete")}>Completos: {assetQuality.classA?.complete ?? 0}</button>
                            <button type="button" className={qualityMetricButtonClass("a_pending_image")} disabled={!hasAssetQualityRows} onClick={() => selectAssetQualityFilter("a_pending_image")}>Pendientes de imagen: {assetQuality.classA?.pendingImage ?? 0}</button>
                            <button type="button" className={qualityMetricButtonClass("a_pending_datasheet")} disabled={!hasAssetQualityRows} onClick={() => selectAssetQualityFilter("a_pending_datasheet")}>Pendientes de ficha: {assetQuality.classA?.pendingDatasheet ?? 0}</button>
                          </div>
                        </div>
                        <button type="button" className={qualityCardClass("b_partial")} disabled={!hasAssetQualityRows} onClick={() => selectAssetQualityFilter("b_partial")}>
                          <div className="text-xs text-muted-foreground">B — Parcial</div>
                          <div className="text-2xl font-semibold">{assetQuality.classB?.total ?? 0}</div>
                          <div className="text-xs text-muted-foreground">Parcial / pendiente externo: {assetQuality.classB?.partial ?? 0}</div>
                        </button>
                        <button type="button" className={qualityCardClass("c_not_applicable")} disabled={!hasAssetQualityRows} onClick={() => selectAssetQualityFilter("c_not_applicable")}>
                          <div className="text-xs text-muted-foreground">C — No aplica</div>
                          <div className="text-2xl font-semibold">{assetQuality.classC?.total ?? 0}</div>
                          <div className="text-xs text-muted-foreground">Sin deuda documental: {assetQuality.classC?.notApplicable ?? 0}</div>
                        </button>
                        <button type="button" className={qualityCardClass("d_manual_review")} disabled={!hasAssetQualityRows} onClick={() => selectAssetQualityFilter("d_manual_review")}>
                          <div className="text-xs text-muted-foreground">D — Revisar</div>
                          <div className="text-2xl font-semibold">{assetQuality.classD?.total ?? 0}</div>
                          <div className="text-xs text-muted-foreground">Revisión manual: {assetQuality.classD?.manualReview ?? 0}</div>
                        </button>
                      </div>
                      {!hasAssetQualityRows && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                          Calidad por assets no disponible en esta carga. Las tarjetas A/B/C/D quedan desactivadas para evitar filtros incompletos.
                        </div>
                      )}
                      <div className="flex flex-col gap-2 rounded-lg border border-primary/30 bg-primary/10 p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                        <div className="font-medium">
                          Filtro activo: {activeAssetQualityFilterText || `Activos operativos · ${displayRows.length} productos`}
                        </div>
                        {activeAssetQualityFilterText && (
                          <Button type="button" size="sm" variant="outline" onClick={() => selectAssetQualityFilter(null)}>
                            Limpiar filtro
                          </Button>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Calidad por assets no disponible en esta carga. El catálogo mantiene la lista operativa sin filtros legacy visibles.</div>
                  )}

                  <div className="hidden md:block">
                    <Table>
                      <TableHeader><TableRow><TableHead>Severidad</TableHead><TableHead>Producto</TableHead><TableHead>Incidencia</TableHead><TableHead>Campo</TableHead><TableHead className="text-right">Acción</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {filteredQualityIssues.slice(0, 80).map((issue: any, index: number) => (
                          <TableRow key={`op-${issue.productId}-${issue.code}-${issue.field || index}`}>
                            <TableCell><Badge variant={qualitySeverityVariant(issue.severity)}>{qualitySeverityLabel(issue.severity)}</Badge></TableCell>
                            <TableCell><div className="font-medium">{issue.sku}</div><div className="text-xs text-muted-foreground">{issue.name}</div></TableCell>
                            <TableCell><div>{issue.label || issue.message}</div><div className="text-xs text-muted-foreground">{familyLabel(issue.family)} · {issue.origin === "wattwin" ? "Wattwin" : "Manual"}</div></TableCell>
                            <TableCell>{issue.field || "—"}</TableCell>
                            <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => openEditProductById(issue.productId)}>Editar</Button></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="space-y-3 md:hidden">
                    {filteredQualityIssues.slice(0, 80).map((issue: any, index: number) => (
                      <div key={`op-mobile-${issue.productId}-${issue.code}-${issue.field || index}`} className="rounded-lg border p-3">
                        <div className="flex items-start justify-between gap-2"><div className="font-medium">{issue.name}</div><Badge variant={qualitySeverityVariant(issue.severity)}>{qualitySeverityLabel(issue.severity)}</Badge></div>
                        <div className="mt-1 text-xs text-muted-foreground">SKU {issue.sku} · {familyLabel(issue.family)}</div>
                        <div className="mt-2 text-sm">{issue.label || issue.message}</div>
                        {issue.field && <div className="mt-1 text-xs text-muted-foreground">Campo: {issue.field}</div>}
                        <Button className="mt-3 w-full" size="sm" variant="outline" onClick={() => openEditProductById(issue.productId)}>Editar</Button>
                      </div>
                    ))}
                  </div>
                  {!filteredQualityIssues.length && <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">Sin incidencias para el filtro seleccionado.</div>}
                </>
              )}
            </CardContent>
          </Card>

          {showWattwinAudit && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5" />Auditoría import Wattwin</CardTitle>
                <CardDescription>Herramienta avanzada: valida que la API/mapeo trae correctamente lo disponible en Wattwin.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {qualityQuery.isLoading ? <div className="text-sm text-muted-foreground">Calculando…</div> : (
                  <>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">Productos Wattwin</div><div className="text-2xl font-semibold">{qualitySummary?.origin?.wattwin ?? 0}</div><div className="text-xs text-muted-foreground">Duplicados posibles: {qualitySummary?.potentialDuplicates ?? 0}</div></div>
                      <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">Incompletos</div><div className="text-2xl font-semibold">{qualitySummary?.incomplete ?? 0}</div><div className="text-xs text-muted-foreground">Sin imagen: {qualitySummary?.missingImage ?? 0}</div></div>
                      <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">Sin ficha técnica</div><div className="text-2xl font-semibold">{qualitySummary?.missingTechnicalSheet ?? 0}</div><div className="text-xs text-muted-foreground">Datos dudosos: {qualitySummary?.suspiciousTechnicalData ?? 0}</div></div>
                      <div className="rounded-lg border p-3"><div className="text-xs text-muted-foreground">Precios</div><div className="text-sm">Compra: {qualitySummary?.withPurchasePrice ?? 0}</div><div className="text-sm">Venta rec.: {qualitySummary?.withSaleRecommended ?? 0}</div></div>
                    </div>

                    <div className="hidden md:block">
                      <Table>
                        <TableHeader><TableRow><TableHead>Severidad</TableHead><TableHead>Producto</TableHead><TableHead>Incidencia</TableHead><TableHead>Campo</TableHead><TableHead className="text-right">Acción</TableHead></TableRow></TableHeader>
                        <TableBody>
                          {qualityIssues.filter((i:any) => i.origin === "wattwin").slice(0, 40).map((issue: any, index: number) => (
                            <TableRow key={`ww-${issue.productId}-${issue.code}-${issue.field || index}`}>
                              <TableCell><Badge variant={qualitySeverityVariant(issue.severity)}>{qualitySeverityLabel(issue.severity)}</Badge></TableCell>
                              <TableCell><div className="font-medium">{issue.sku}</div><div className="text-xs text-muted-foreground">{issue.name}</div></TableCell>
                              <TableCell><div>{issue.label || issue.message}</div><div className="text-xs text-muted-foreground">{familyLabel(issue.family)}</div></TableCell>
                              <TableCell>{issue.field || "—"}</TableCell>
                              <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => openEditProductById(issue.productId)}>Editar</Button></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {!qualityIssues.filter((i:any) => i.origin === "wattwin").length && <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">Sin incidencias para el filtro seleccionado.</div>}
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Productos</CardTitle>
            <CardDescription>Búsqueda por SKU, nombre, marca, modelo o alias.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-4">
              <div className="relative">
                <Input
                  id="product-catalog-search"
                  name="product-catalog-search"
                  type="search"
                  autoComplete="off"
                  placeholder="Buscar por SKU, nombre, marca..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground px-1 py-0.5 rounded border"
                    onClick={() => setSearch("")}
                    title="Limpiar filtros"
                  >
                    ✕
                  </button>
                )}
              </div>
              <Select value={family} onValueChange={setFamily}>
                <SelectTrigger><SelectValue placeholder="Familia" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las familias</SelectItem>
                  {FAMILIES.map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={active} onValueChange={setActive}>
                <SelectTrigger><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Activos</SelectItem>
                  <SelectItem value="false">Inactivos</SelectItem>
                  <SelectItem value="all">Todos</SelectItem>
                </SelectContent>
              </Select>
              {activeAssetQualityFilterText && (
                <Button type="button" variant="outline" onClick={() => selectAssetQualityFilter(null)}>
                  Limpiar filtro de calidad
                </Button>
              )}
            </div>

            {activeAssetQualityFilterText && (
              <div className="flex flex-col gap-2 rounded-lg border border-primary/40 bg-primary/10 p-3 text-sm md:flex-row md:items-center md:justify-between">
                <div className="font-medium">Filtro activo: {activeAssetQualityFilterText}</div>
                <Button type="button" size="sm" variant="outline" onClick={() => selectAssetQualityFilter(null)}>
                  Limpiar filtro de calidad
                </Button>
              </div>
            )}

            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Imagen</TableHead><TableHead>SKU</TableHead><TableHead>Producto</TableHead><TableHead>Familia</TableHead><TableHead>Proveedor</TableHead><TableHead>Precios catálogo</TableHead><TableHead>Origen</TableHead><TableHead>Estado</TableHead><TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayRows.map((row: any) => {
                    const product = row.product;
                    const externalRef = row.externalRef;
                    const productAssets = assetsByProduct[product.id] || [];
                    const primaryImage = getProductPrimaryImage(productAssets);
                    const hasTechnicalSheet = cachedDocumentAssetsByPurpose(productAssets, "technical_sheet").length > 0;
                    const qualityBadges = productQualityBadges(qualityIssuesByProduct[product.id] || []);
                    return (
                      <TableRow key={product.id}>
                        <TableCell>
                          <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded border bg-muted/20 text-center text-[10px] text-muted-foreground">
                            {primaryImage ? <img src={primaryImage.viewUrl} alt={product.name} className="h-full w-full object-contain object-center" /> : <span className="px-1">Sin imagen</span>}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">{product.sku}</TableCell>
                        <TableCell>{product.name}<div className="text-xs text-muted-foreground">{[product.brand, product.model].filter(Boolean).join(" · ") || "Sin marca/modelo"}</div></TableCell>
                        <TableCell><div>{familyLabel(product.family)}</div><div className="text-xs text-muted-foreground">{categoryDisplayLabel(product.category)}</div></TableCell>
                        <TableCell>{row.supplier?.name || "—"}</TableCell>
                        <TableCell>
                          <div className="text-sm">Compra: {formatCatalogPrice(product.purchasePriceCurrent)}</div>
                          <div className="text-xs text-muted-foreground">Venta rec.: {formatCatalogPrice(product.salePriceRecommended)}</div>
                          <div className="text-xs text-muted-foreground">Margen prev.: {expectedMargin(product.purchasePriceCurrent, product.salePriceRecommended)}</div>
                        </TableCell>
                        <TableCell>{externalRef ? <Badge variant="outline">Wattwin</Badge> : "—"}</TableCell>
                        <TableCell><div className="flex flex-wrap gap-1"><Badge variant={product.active ? "default" : "secondary"}>{product.active ? "Activo" : "Incompleto/borrador"}</Badge>{qualityBadges.length ? qualityBadges.map((label) => <Badge key={label} variant="outline">{label}</Badge>) : <Badge variant="default">Completo</Badge>}{!primaryImage && !qualityBadges.includes("Sin imagen") && <Badge variant="outline">Sin imagen</Badge>}{!hasTechnicalSheet && !qualityBadges.includes("Sin ficha") && <Badge variant="outline">Sin ficha técnica</Badge>}</div></TableCell>
                        <TableCell>
                          <div className="flex flex-wrap justify-end gap-2">
                            <Button type="button" size="sm" variant="outline" aria-label={`Ver ${product.name}`} title="Ver" onClick={() => { setDetailProduct({ ...product, externalRef }); setDetailDialogOpen(true); }}><Eye className="h-4 w-4" /></Button>
                            <Button type="button" size="sm" variant="outline" aria-label={`Editar ${product.name}`} title="Editar" onClick={(event) => { event.preventDefault(); event.stopPropagation(); openEditProduct(product); }}><Pencil className="mr-2 h-4 w-4" />Editar</Button>
                            <Button type="button" size="sm" variant="outline" aria-label={`Alias de ${product.name}`} title="Alias" onClick={() => { setSelectedProductId(product.id); setAliasDialogOpen(true); }}><Tags className="h-4 w-4" /></Button>
                            <Button type="button" size="sm" variant="outline" disabled={setProductActive.isPending} onClick={() => setProductActive.mutate({ id: product.id, active: !product.active })}>{product.active ? "Desactivar" : "Activar"}</Button>
                            {canDeleteProductInStagingUi && (
                              <Button
                                type="button"
                                size="sm"
                                variant="destructive"
                                aria-label={`Eliminar ${product.name}`}
                                title="Eliminar solo staging"
                                onClick={() => openDeleteProduct(product, externalRef)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="space-y-3 md:hidden">
              {displayRows.map((row: any) => {
                const product = row.product;
                const externalRef = row.externalRef;
                const productAssets = assetsByProduct[product.id] || [];
                const primaryImage = getProductPrimaryImage(productAssets);
                const hasTechnicalSheet = cachedDocumentAssetsByPurpose(productAssets, "technical_sheet").length > 0;
                const qualityBadges = productQualityBadges(qualityIssuesByProduct[product.id] || []);
                return (
                  <div key={product.id} className="rounded-lg border bg-card p-3 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded border bg-muted/20 text-center text-[11px] text-muted-foreground">
                        {primaryImage ? <img src={primaryImage.viewUrl} alt={product.name} className="h-full w-full object-contain object-center" /> : <span className="px-1">Sin imagen</span>}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold leading-tight">{product.name}</div>
                        <div className="mt-1 text-xs text-muted-foreground">SKU {product.sku}</div>
                        <div className="mt-1 text-xs text-muted-foreground">{[product.brand, product.model].filter(Boolean).join(" · ") || "Sin marca/modelo"}</div>
                      </div>
                      <Badge variant={product.active ? "default" : "secondary"}>{product.active ? "Activo" : "Incompleto"}</Badge>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Badge variant="secondary">{familyLabel(product.family)}</Badge>
                      {externalRef && <Badge variant="outline">Origen Wattwin</Badge>}
                      {product.category && <Badge variant="outline">{categoryDisplayLabel(product.category)}</Badge>}
                      {qualityBadges.length ? qualityBadges.map((label) => <Badge key={label} variant="outline">{label}</Badge>) : <Badge variant="default">Completo</Badge>}
                      {!primaryImage && !qualityBadges.includes("Sin imagen") && <Badge variant="outline">Sin imagen</Badge>}
                      {!hasTechnicalSheet && !qualityBadges.includes("Sin ficha") && <Badge variant="outline">Sin ficha técnica</Badge>}
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-muted-foreground">Proveedor</span><div>{row.supplier?.name || "—"}</div></div>
                      <div><span className="text-muted-foreground">Compra catálogo</span><div>{formatCatalogPrice(product.purchasePriceCurrent)}</div></div>
                      <div><span className="text-muted-foreground">Venta rec.</span><div>{formatCatalogPrice(product.salePriceRecommended)}</div></div>
                      <div><span className="text-muted-foreground">Margen previsto</span><div>{expectedMargin(product.purchasePriceCurrent, product.salePriceRecommended)}</div></div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <Button type="button" size="sm" variant="outline" onClick={() => { setDetailProduct({ ...product, externalRef }); setDetailDialogOpen(true); }}><Eye className="mr-2 h-4 w-4" />Ver</Button>
                      <Button type="button" size="sm" variant="outline" onClick={(event) => { event.preventDefault(); event.stopPropagation(); openEditProduct(product); }}><Pencil className="mr-2 h-4 w-4" />Editar</Button>
                      <Button type="button" size="sm" variant="outline" onClick={() => { setSelectedProductId(product.id); setAliasDialogOpen(true); }}><Tags className="mr-2 h-4 w-4" />Alias</Button>
                      <Button type="button" size="sm" variant="outline" disabled={setProductActive.isPending} onClick={() => setProductActive.mutate({ id: product.id, active: !product.active })}>{product.active ? "Desactivar" : "Activar"}</Button>
                      {canDeleteProductInStagingUi && (
                        <Button type="button" size="sm" variant="destructive" onClick={() => openDeleteProduct(product, externalRef)}>
                          <Trash2 className="mr-2 h-4 w-4" />Eliminar
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {canShowWattwinAdminTools && (
        <Card>
          <CardHeader>
            <CardTitle>Herramientas Wattwin / Avanzado <Badge variant="outline" className="ml-2 text-[10px]">Solo staging</Badge></CardTitle>
            <CardDescription>
              Flujo ordenado para staging: la clave se usa una vez desde esta pantalla, no se guarda y nunca modifica Wattwin.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
              <div>
                <Label>Clave API Wattwin</Label>
                <Input
                  type="password"
                  autoComplete="off"
                  value={wattwinApiKey}
                  onChange={(e) => setWattwinApiKey(e.target.value)}
                  placeholder="Pegar clave solo para esta ejecución"
                />
                <p className="mt-1 text-xs text-muted-foreground">No se guarda en localStorage/sessionStorage ni en configuración. Se limpia al terminar cada acción que la usa.</p>
              </div>
              <div className="text-xs text-muted-foreground md:max-w-sm">
                Ejecuta primero la auditoría. La normalización canonical solo se habilita si la auditoría devuelve 7/7 OK y sin errores generales.
              </div>
            </div>

            <div className="grid gap-3 lg:grid-cols-4">
              <div className="rounded-lg border p-3">
                <div className="mb-2 text-sm font-medium">1. Auditar 7 productos Wattwin</div>
                <p className="mb-3 text-xs text-muted-foreground">Dry-run sin escrituras en BD. Compara API live con catálogo ODS y muestra matriz por SKU.</p>
                <Button
                  className="w-full"
                  disabled={!canRunWattwinAdmin || !wattwinApiKey.trim() || wattwinToolBusy}
                  onClick={() => deepReconcileWattwinSample.mutate({ source: "wattwin", mode: "dry_run", apiKey: wattwinApiKey, limit: 7 })}
                >
                  {deepReconcileWattwinSample.isPending ? "Auditando…" : "Auditar 7 productos Wattwin"}
                </Button>
              </div>
              <div className="rounded-lg border p-3">
                <div className="mb-2 text-sm font-medium">2. Aplicar normalización canonical</div>
                <p className="mb-3 text-xs text-muted-foreground">Bloqueado en este MVP preview-only. No se ejecutan escrituras Wattwin desde esta rama.</p>
                <Button
                  className="w-full"
                  variant="outline"
                  disabled
                  onClick={() => deepReconcileWattwinSample.mutate({ source: "wattwin", mode: "apply_canonical_7", apiKey: wattwinApiKey, limit: 7 })}
                >
                  {deepReconcileWattwinSample.isPending ? "Aplicando…" : "Aplicar normalización canonical"}
                </Button>
                {!wattwinDryRunOk && <p className="mt-2 text-xs text-amber-700">{wattwinDryRunReason}</p>}
              </div>
              <div className="rounded-lg border p-3">
                <div className="mb-2 text-sm font-medium">3. Resolver imágenes y documentos (muestra de 25)</div>
                <p className="mb-3 text-xs text-muted-foreground">Bloqueado en este MVP preview-only. No escribe product_assets ni S3.</p>
                <Button
                  className="w-full"
                  variant="outline"
                  disabled
                  title="Bloqueado en MVP1 preview-only"
                  onClick={() => resolveWattwinAssetsSample.mutate({ source: "wattwin", mode: "resolve_existing_sample_assets", apiKey: wattwinApiKey, limit: 25, assetTypes: ["main_image", "gallery_image", "technical_sheet", "catalog", "certificate", "manual", "video", "other"] })}
                >
                  {resolveWattwinAssetsSample.isPending ? "Resolviendo…" : "Resolver imágenes y documentos"}
                </Button>
              </div>
              <div className="rounded-lg border p-3">
                <div className="mb-2 text-sm font-medium">4. Reclasificar documentos</div>
                <p className="mb-3 text-xs text-muted-foreground">Bloqueado en este MVP preview-only. No modifica product_assets.</p>
                <Button
                  className="w-full"
                  variant="outline"
                  disabled
                  title="Bloqueado en MVP1 preview-only"
                  onClick={() => reclassifyWattwinDocumentAssets.mutate()}
                >
                  {reclassifyWattwinDocumentAssets.isPending ? "Reclasificando…" : "Reclasificar documentos"}
                </Button>
              </div>
            </div>

            <div className="rounded border border-blue-200 bg-blue-50/50 p-3">
              <div className="mb-1 text-sm font-medium text-foreground">5. Importar Top 10 Wattwin en staging</div>
              <p className="mb-3 text-xs text-muted-foreground">Bloqueado en este MVP preview-only. La importación seleccionada queda para MVP2.</p>
              <div className="mb-3 space-y-1 text-xs">
                {TOP10_SKU_LABELS.map((label) => (
                  <div key={label} className="flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
              <Button
                className="w-full"
                size="sm"
                disabled
                onClick={() => setImportConfirmOpen(true)}
              >
                {runWattwinImport.isPending ? "Importando…" : "Importar Top 10 Wattwin en staging"}
              </Button>
            </div>

            <Dialog open={importConfirmOpen} onOpenChange={setImportConfirmOpen}>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Importar Top 10 Wattwin</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                  <p className="text-amber-700">Esta acción importará 10 productos desde Wattwin al catálogo de staging.</p>
                  <ul className="list-inside list-disc space-y-1 text-xs text-muted-foreground">
                    {TOP10_SKU_LABELS.map((label) => (
                      <li key={label}>{label}</li>
                    ))}
                  </ul>
                  <p className="text-xs text-muted-foreground">No modifica Wattwin. La clave no se guarda.</p>
                </div>
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setImportConfirmOpen(false)}>Cancelar</Button>
                  <Button
                    onClick={() => {
                      setImportConfirmOpen(false);
                      runWattwinImport.mutate({ source: "wattwin", mode: "sample", apiKey: wattwinApiKey, limit: 10, productIds: TOP10_IMPORT_PRODUCTS });
                    }}
                  >
                    Importar 10 productos
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {wattwinDryRunSummary && (
              <div className="rounded border p-3 text-sm">
                <div className="font-medium">Auditoría Wattwin dry-run</div>
                <div className="mt-2 grid gap-2 md:grid-cols-3">
                  <div>Modo: {wattwinDryRunSummary.mode || "dry_run"}</div>
                  <div>OK: {wattwinDeepSummary(wattwinDryRunSummary).ok}/{wattwinDeepSummary(wattwinDryRunSummary).total}</div>
                  <div>Pendientes/error: {wattwinDeepSummary(wattwinDryRunSummary).endpointPending}</div>
                </div>
                {!!wattwinDeepRows(wattwinDryRunSummary).length && (
                  <div className="mt-3 overflow-x-auto">
                    <Table>
                      <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Familia</TableHead><TableHead>Estado</TableHead><TableHead>Faltantes</TableHead><TableHead>Warnings</TableHead><TableHead>Campos propuestos/detectados</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {wattwinDeepRows(wattwinDryRunSummary).map((item: any) => (
                          <TableRow key={`${item.productId}-${item.sku}`}>
                            <TableCell>{item.sku || "—"}<div className="text-xs text-muted-foreground">{item.name || item.producto || "—"}</div></TableCell>
                            <TableCell>{familyLabel(item.family || item.familia)}</TableCell>
                            <TableCell>{item.status === "ok" ? "OK" : item.status || "—"}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{fieldList(item.missing || item.missingFields || item.specsComparison?.pendingMappingKeys)}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{fieldList(item.warnings || (item.apiStatus ? [item.apiStatus] : []))}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{proposedCanonicalFields(item)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}

            {wattwinCanonicalSummary && (
              <div className="rounded border p-3 text-sm">
                <div className="font-medium">Normalización canonical aplicada</div>
                <div className="mt-2 grid gap-2 md:grid-cols-3">
                  <div>Modo: {wattwinCanonicalSummary.mode || "apply_canonical_7"}</div>
                  <div>OK: {wattwinDeepSummary(wattwinCanonicalSummary).ok}/{wattwinDeepSummary(wattwinCanonicalSummary).total}</div>
                  <div>Pendientes/error: {wattwinDeepSummary(wattwinCanonicalSummary).endpointPending}</div>
                </div>
                {!!wattwinDeepRows(wattwinCanonicalSummary).length && (
                  <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                    {wattwinDeepRows(wattwinCanonicalSummary).map((item: any) => <div key={`${item.productId}-${item.sku}`}>{item.sku || "—"} · {item.status || "—"} · {proposedCanonicalFields(item)}</div>)}
                  </div>
                )}
              </div>
            )}

            {wattwinAssetsSummary && (
              <div className="rounded border p-3 text-sm">
                <div className="font-medium">Assets Wattwin sanitizados</div>
                <div className="mt-2 grid gap-2 md:grid-cols-4">
                  <div>Estado: {wattwinAssetStatusText(wattwinAssetsSummary.status)}</div>
                  <div>Revisados: {wattwinAssetsSummary.checked ?? 0}</div>
                  <div>Imágenes: {cachedByPurpose(wattwinAssetsSummary, ["main_image", "gallery_image"])} cacheadas / {pendingByPurpose(wattwinAssetsSummary, ["main_image", "gallery_image"])} pendientes</div>
                  <div>Fichas técnicas: {cachedByPurpose(wattwinAssetsSummary, ["technical_sheet"])} cacheadas / {pendingByPurpose(wattwinAssetsSummary, ["technical_sheet"])} pendientes</div>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">Motivo principal: {wattwinAssetsSummary.mainReason || "—"}</div>
                {!!wattwinAssetsSummary.rows?.length && (
                  <details className="mt-3 rounded border bg-muted/20 p-2 text-xs text-muted-foreground">
                    <summary className="cursor-pointer font-medium text-foreground">Ver diagnóstico</summary>
                    <div className="mt-2 space-y-2">
                      {wattwinAssetsSummary.rows.map((item: any) => (
                        <div key={`${item.productId}-${item.sku}`}>
                          <div className="font-medium text-foreground">{item.sku} · {item.producto}</div>
                          <div>productExternalId {item.productExternalId || "—"} · rawPayload {item.rawPayloadFound ? "sí" : "no"} · mediaRefs {item.mediaRefsFound ?? 0} · docRefs {item.docRefsFound ?? 0} · docsByRelatedId {item.docsByRelatedId ?? 0} · refsInProduct {item.refsInProduct ?? 0} · candidateAssets {item.candidateAssets ?? item.assets ?? 0} · cacheados {item.cached ?? 0} · pendientes {item.pending ?? 0} · fallidos {item.failed ?? 0}{item.warnings?.length ? ` · ${item.warnings.join(" · ")}` : ""}</div>
                          {!!item.filterVariants?.length && (
                            <div className="mt-1 space-y-1 pl-2">
                              {item.filterVariants.map((variant: any, idx: number) => (
                                <div key={`${item.productId}-filter-${idx}`}>Documents filter {variant.variant}: status {variant.status ?? "—"} · count {variant.count ?? 0}{variant.error ? ` · ${variant.error}` : ""} · {variant.path || "—"}</div>
                              ))}
                            </div>
                          )}
                          {!!item.diagnostics?.length && (
                            <div className="mt-1 space-y-1 pl-2">
                              {item.diagnostics.map((diag: any, idx: number) => (
                                <div key={`${item.productId}-${diag.purpose}-${idx}`}>
                                  {diag.purpose} · campo {diag.sourceField || "—"} · endpoint {Array.isArray(diag.endpointTried) ? diag.endpointTried.join(" + ") : diag.endpointTried || "—"} · descarga {diag.directDownloadFound ? "sí" : "no"} · auth {diag.requiresAuth ? "sí" : "no"} · {diag.result || "—"} · {diag.reason || "—"}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
            {wattwinReclassifySummary && (
              <div className="rounded border p-3 text-sm">
                <div className="font-medium">Reclasificación documentos Wattwin</div>
                <div className="mt-2 grid gap-2 md:grid-cols-3">
                  <div>Revisados: {wattwinReclassifySummary.assetsReviewed ?? 0}</div>
                  <div>Reclasificados: {wattwinReclassifySummary.assetsReclassified ?? 0}</div>
                  <div>Sin cambios: {wattwinReclassifySummary.unchanged ?? 0}</div>
                  <div>Ficha técnica: {wattwinReclassifySummary.otherToTechnicalSheet ?? 0}</div>
                  <div>Catálogo: {wattwinReclassifySummary.otherToCatalog ?? 0}</div>
                  <div>Certificado/manual: {(wattwinReclassifySummary.otherToCertificate ?? 0) + (wattwinReclassifySummary.otherToManual ?? 0)}</div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        )}
      </div>

      <Dialog open={wattwinPreviewOpen} onOpenChange={(open) => { if (!open) closeWattwinPreview(); else setWattwinPreviewOpen(true); }}>
        <DialogContent className="flex flex-col w-screen h-screen md:w-[95vw] md:max-w-[1400px] md:h-[95vh] md:rounded-lg p-0 gap-0 border-0 md:border">
          {/* Sticky header */}
          <div className="flex-none px-4 pt-4 pb-3 border-b bg-background">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold">Sincronizar catálogo Wattwin ODS</h2>
                <p className="text-sm text-muted-foreground mt-0.5">Previsualización solamente · No se modifica la base de datos</p>
              </div>
            </div>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">

            {/* Filters — compact grid */}
            <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
              <div className="col-span-2 sm:col-span-2 lg:col-span-2">
                <Label className="text-xs">Buscar</Label>
                <Input value={wattwinPreviewText} onChange={(e) => setWattwinPreviewText(e.target.value)} placeholder="Nombre, modelo, SKU o referencia" className="h-8 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Fabricante / marca</Label>
                <Input value={wattwinPreviewManufacturer} onChange={(e) => setWattwinPreviewManufacturer(e.target.value)} placeholder="Goodwe, Trina..." className="h-8 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Familia</Label>
                <Select value={wattwinPreviewFamily} onValueChange={setWattwinPreviewFamily}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {FAMILIES.map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Estado</Label>
                <Select value={wattwinPreviewStatus} onValueChange={setWattwinPreviewStatus}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="new_product">Pendientes de traer a App ODS</SelectItem>
                    <SelectItem value="update_existing">Cambios detectados</SelectItem>
                    <SelectItem value="possible_duplicate">Posibles duplicados</SelectItem>
                    <SelectItem value="manual_review">Revisión manual</SelectItem>
                    <SelectItem value="already_imported">Ya existen en App ODS</SelectItem>
                    <SelectItem value="skip_low_relevance">Baja relevancia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Límite visual</Label>
                <Input inputMode="numeric" value={wattwinPreviewLimit} onChange={(e) => setWattwinPreviewLimit(e.target.value)} className="h-8 text-sm" />
              </div>
            </div>
            {/* Switches inline */}
            <div className="flex flex-wrap gap-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch checked={wattwinPreviewOnlyNotImported} onCheckedChange={setWattwinPreviewOnlyNotImported} />
                <span>Solo no importados</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch checked={wattwinPreviewOnlyWithImage} onCheckedChange={setWattwinPreviewOnlyWithImage} />
                <span>Con imagen</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Switch checked={wattwinPreviewOnlyWithTechnicalSheet} onCheckedChange={setWattwinPreviewOnlyWithTechnicalSheet} />
                <span>Con ficha técnica</span>
              </label>
            </div>

            {/* Action bar */}
            <div className="flex flex-wrap gap-2 items-center justify-between">
              <Button type="button" onClick={runWattwinCatalogPreview} disabled={wattwinCatalogPreviewQuery.isFetching}>
                <RefreshCw className="mr-2 h-4 w-4" />{wattwinCatalogPreviewQuery.isFetching ? "Escaneando catálogo Wattwin…" : "Detectar novedades"}
              </Button>
              <div className="flex gap-2">
                <Button type="button" variant="outline" disabled={!wattwinPreviewRows.length} onClick={() => downloadTextFile("wattwin-catalog-preview.json", JSON.stringify(wattwinCatalogPreviewQuery.data, null, 2), "application/json")}>Export JSON</Button>
                <Button type="button" variant="outline" disabled={!wattwinPreviewRows.length} onClick={() => downloadTextFile("wattwin-catalog-preview.csv", wattwinPreviewCsv(wattwinPreviewRows), "text/csv;charset=utf-8")}>Export CSV</Button>
              </div>
            </div>

            {/* Error / notice */}
            {wattwinPreviewNotice && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{wattwinPreviewNotice}</div>
            )}
            {wattwinCatalogPreviewQuery.error && !wattwinPreviewNotice && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                No se pudo consultar Wattwin en staging. Revisa la configuración del conector. No se ha modificado ningún dato.
              </div>
            )}

            {/* Scan status bar */}
            {wattwinCatalogPreviewQuery.data && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 flex flex-wrap gap-3">
                <span>Escaneados: <strong>{(wattwinCatalogPreviewQuery.data as any).scannedCount ?? 0}</strong></span>
                <span>Total catálogo Wattwin: <strong>{(wattwinCatalogPreviewQuery.data as any).wattwinCatalogTotal ?? "—"}</strong></span>
                {(wattwinCatalogPreviewQuery.data as any).partialScan && (
                  <span className="text-amber-700 font-medium">Escaneo parcial</span>
                )}
              </div>
            )}

            {/* Global summary — horizontal scrollable cards */}
            {wattwinGlobalSummary && (
              <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Resumen global del catálogo Wattwin escaneado</div>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  <div className="rounded-md border p-2 text-center min-w-[120px] shrink-0"><div className="text-xs text-muted-foreground">Pendientes de traer a App ODS</div><div className="text-xl font-semibold text-blue-700">{wattwinGlobalSummary.newProducts ?? 0}</div></div>
                  <div className="rounded-md border p-2 text-center min-w-[120px] shrink-0"><div className="text-xs text-muted-foreground">Ya existen en App ODS</div><div className="text-xl font-semibold">{wattwinGlobalSummary.alreadyImported ?? 0}</div></div>
                  <div className="rounded-md border p-2 text-center min-w-[120px] shrink-0"><div className="text-xs text-muted-foreground">Cambios detectados</div><div className="text-xl font-semibold">{wattwinGlobalSummary.updates ?? 0}</div></div>
                  <div className="rounded-md border p-2 text-center min-w-[120px] shrink-0"><div className="text-xs text-muted-foreground">Posibles duplicados</div><div className="text-xl font-semibold">{wattwinGlobalSummary.possibleDuplicates ?? 0}</div></div>
                  <div className="rounded-md border p-2 text-center min-w-[120px] shrink-0"><div className="text-xs text-muted-foreground">Revisión manual</div><div className="text-xl font-semibold">{wattwinGlobalSummary.manualReview ?? 0}</div></div>
                  <div className="rounded-md border p-2 text-center min-w-[120px] shrink-0"><div className="text-xs text-muted-foreground">Baja relevancia</div><div className="text-xl font-semibold text-muted-foreground">{wattwinGlobalSummary.skipLowRelevance ?? 0}</div></div>
                </div>
              </div>
            )}

            {/* Selection bar */}
            {wattwinPreviewRows.length > 0 && (
              <div className="flex flex-wrap items-center gap-3 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={allVisibleSelected}
                    onCheckedChange={toggleSelectAllVisible}
                  />
                  <span>Seleccionar todos ({allVisibleIds.length})</span>
                </label>
                <span className="font-medium">Seleccionados: {wattwinSelectedCount}</span>
                {wattwinSelectedCount > 0 && (
                  <Button type="button" size="sm" variant="ghost" onClick={clearWattwinSelection}>
                    Limpiar
                  </Button>
                )}
                {wattwinSelectedCount > 0 && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const ids = Array.from(wattwinSelectedIds);
                      downloadTextFile("wattwin-selected-ids.json", JSON.stringify(ids, null, 2), "application/json");
                    }}
                  >
                    Copiar IDs seleccionados
                  </Button>
                )}
              </div>
            )}

            {/* Mobile cards */}
            <div className="space-y-3 md:hidden">
              {wattwinPreviewRows.map((row: any) => {
                const rowId = row.wattwinProductId || `${row.name}-${row.modelRef}`;
                const isSelected = wattwinSelectedIds.has(row.wattwinProductId);
                return (
                  <div key={rowId} className={`rounded-lg border p-3 transition ${isSelected ? "border-primary bg-primary/5" : ""}`}>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => row.wattwinProductId && toggleWattwinSelection(row.wattwinProductId)}
                        className="mt-0.5 shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-medium leading-tight">{row.name || "—"}</div>
                          <Badge variant={previewStatusVariant(row.suggestedStatus)}>{previewStatusLabel(row.suggestedStatus)}</Badge>
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">{[row.manufacturer, row.modelRef].filter(Boolean).join(" · ") || "Sin marca/modelo"}</div>
                        <div className="mt-2 grid grid-cols-2 gap-1 text-xs text-muted-foreground">
                          <div>Familia: {row.familyDetected ? familyLabel(row.familyDetected) : "—"}</div>
                          <div>En ODS: {row.existsInOds ? "Sí" : "No"}</div>
                          <div>Imagen: {yesNo(row.hasMainImage)}</div>
                          <div>Ficha: {yesNo(row.hasTechnicalSheet)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {!wattwinPreviewRows.length && (
                <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">Pulsa «Detectar novedades» para escanear el catálogo Wattwin ODS.</div>
              )}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block rounded-md border overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={allVisibleSelected}
                          onCheckedChange={toggleSelectAllVisible}
                          disabled={allVisibleIds.length === 0}
                        />
                      </TableHead>
                      <TableHead className="w-[38%]">Producto Wattwin</TableHead>
                      <TableHead>Familia</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>En ODS</TableHead>
                      <TableHead>Assets</TableHead>
                      <TableHead className="max-w-[180px]">Razones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {wattwinPreviewRows.map((row: any) => {
                      const rowId = row.wattwinProductId || `${row.name}-${row.modelRef}`;
                      const isSelected = wattwinSelectedIds.has(row.wattwinProductId);
                      return (
                        <TableRow key={rowId} className={isSelected ? "bg-primary/5" : ""}>
                          <TableCell>
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => row.wattwinProductId && toggleWattwinSelection(row.wattwinProductId)}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{row.name || "—"}</div>
                            <div className="text-xs text-muted-foreground">{[row.manufacturer, row.modelRef].filter(Boolean).join(" · ") || "Sin marca/modelo"}</div>
                          </TableCell>
                          <TableCell>
                            <div>{row.familyDetected ? familyLabel(row.familyDetected) : "—"}</div>
                            <div className="text-xs text-muted-foreground">{row.categoryCode || row.wattwinCategoryCode || "Sin categoría"}</div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={previewStatusVariant(row.suggestedStatus)}>{previewStatusLabel(row.suggestedStatus)}</Badge>
                            <div className="mt-1 text-xs text-muted-foreground">Conf. {Math.round((Number(row.matchConfidence) || 0) * 100)}%</div>
                          </TableCell>
                          <TableCell className="text-sm">{row.existsInOds ? "Sí" : "No"}</TableCell>
                          <TableCell className="text-xs">
                            <div>Imagen: {yesNo(row.hasMainImage)}</div>
                            <div>Ficha: {yesNo(row.hasTechnicalSheet || row.hasDocumentIds)}</div>
                          </TableCell>
                          <TableCell className="max-w-[180px] text-xs text-muted-foreground">{fieldList(row.reasons, 2)}</TableCell>
                        </TableRow>
                      );
                    })}
                    {!wattwinPreviewRows.length && (
                      <TableRow><TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">Pulsa «Detectar novedades» para escanear el catálogo Wattwin ODS.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

          </div>{/* end scrollable body */}

          {!wattwinImportDryRunOpen && (
            <div className="flex-none border-t px-4 py-3 bg-background">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-muted-foreground">
                  {wattwinSelectedCount > 0
                    ? `${wattwinSelectedCount} seleccionado(s) · La selección se usará en la próxima fase · No se importa nada ahora`
                    : "Selecciona productos para prepararlos para la próxima fase"}
                </p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Button variant="outline" className="w-full sm:w-auto" onClick={closeWattwinPreview}>Cerrar</Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full sm:w-auto"
                    disabled={wattwinSelectedCount === 0 || wattwinImportDryRunLoading}
                    onClick={runWattwinImportDryRun}
                    title="Genera un plan de importación sin modificar la base de datos"
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    {wattwinImportDryRunLoading ? 'Analizando…' : `Previsualizar importación${wattwinSelectedCount > 0 ? ` (${wattwinSelectedCount})` : ''}`}
                  </Button>
                  <Button
                    disabled
                    className="w-full sm:w-auto opacity-60"
                    title="Importación pausada hasta que staging tenga un modo de importación autorizado."
                  >
                    Importación pausada
                  </Button>
                </div>
              </div>
            </div>
          )}

        </DialogContent>
      </Dialog>

      <Dialog open={wattwinImportDryRunOpen} onOpenChange={setWattwinImportDryRunOpen}>
        <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] flex flex-col gap-0 p-0" showCloseButton={false}>
          {/* Sticky header with explicit close button (min 44×44px touch target for mobile) */}
          <div className="flex items-start justify-between gap-2 px-6 pt-5 pb-3 shrink-0 border-b">
            <DialogHeader className="flex-1">
              <DialogTitle>Previsualización de importación — Dry-run</DialogTitle>
            </DialogHeader>
            <Button
              variant="ghost"
              size="icon"
              className="min-h-[44px] min-w-[44px] -mt-1 -mr-2 shrink-0"
              onClick={() => setWattwinImportDryRunOpen(false)}
              aria-label="Cerrar"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          {/* Scrollable body */}
          <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-950">
              Dry-run. No se ha modificado la base de datos. Este es un plan de lo que se importaría.
            </div>
            {wattwinImportDryRunError && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                {wattwinImportDryRunError}
              </div>
            )}
            {wattwinImportDryRunLoading && (
              <div className="py-8 text-center text-sm text-muted-foreground">Analizando productos seleccionados en Wattwin…</div>
            )}
            {wattwinImportDryRunResult?.plans?.map((plan: any, idx: number) => (
              <div key={plan.wattwinProductId ?? idx} className="rounded-lg border p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium">{plan.proposedProduct?.name ?? plan.wattwinProductId}</div>
                  <Badge
                    variant={wattwinActionVariant(plan.action)}
                    className="cursor-default bg-muted text-muted-foreground hover:bg-muted"
                  >
                    {wattwinActionLabel(plan.action)}
                  </Badge>
                </div>
                {plan.proposedProduct && (
                  <div className="grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
                    <div>Fabricante: {displayWattwinManufacturer(plan.proposedProduct.manufacturer)}</div>
                    <div>Modelo: {plan.proposedProduct.modelReference ?? '—'}</div>
                    <div>Familia: {plan.proposedProduct.family ? familyLabel(plan.proposedProduct.family) : '—'}{plan.proposedProduct.family ? <span className="ml-1 text-muted-foreground/60">({plan.proposedProduct.family})</span> : null}</div>
                    <div>Estado sugerido: {plan.proposedProduct.status}</div>
                  </div>
                )}
                {plan.gateResult && (
                  <div className={`rounded-md border p-2 text-xs ${plan.gateResult.ok ? 'border-green-200 bg-green-50 text-green-900' : 'border-red-200 bg-red-50 text-red-900'}`}>
                    <div className="font-medium mb-1">{plan.gateResult.ok ? '✓ Gate técnico OK' : '✗ Faltan datos técnicos mínimos'}</div>
                    {!plan.gateResult.ok && plan.gateResult.missing.length > 0 && (
                      <div>Obligatorios ausentes: {plan.gateResult.missing.map((k: string) => CANONICAL_SPEC_META[k]?.label ?? k).join(', ')}</div>
                    )}
                    {plan.gateResult.warnings.length > 0 && (
                      <div className="text-amber-700 mt-1">Advertencias: {plan.gateResult.warnings.map((k: string) => CANONICAL_SPEC_META[k]?.label ?? k).join(' · ')}</div>
                    )}
                  </div>
                )}
                {plan.canonicalSpecsPreview && Object.keys(plan.canonicalSpecsPreview).length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-muted-foreground">Datos técnicos que se crearán ({Object.keys(plan.canonicalSpecsPreview).length} campos)</summary>
                    <div className="mt-2 space-y-3">
                      {(() => {
                        const groups: Record<string, [string, unknown][]> = {};
                        for (const [k, v] of Object.entries(plan.canonicalSpecsPreview)) {
                          const group = wattwinSpecGroupLabel(CANONICAL_SPEC_META[k]?.group ?? "Datos adicionales de Wattwin");
                          if (!groups[group]) groups[group] = [];
                          groups[group].push([k, v]);
                        }
                        const sorted = Object.keys(groups).sort((a, b) => {
                          const ai = CANONICAL_SPEC_GROUP_ORDER.indexOf(a);
                          const bi = CANONICAL_SPEC_GROUP_ORDER.indexOf(b);
                          return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                        });
                        return sorted.map(group => (
                          <div key={group}>
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70 mb-1">{group}</div>
                            <div className={`grid gap-1 pl-1 ${group === "Certificaciones" ? "sm:grid-cols-1" : "sm:grid-cols-2"}`}>
                              {groups[group].map(([k, v]) => {
                                const meta = CANONICAL_SPEC_META[k];
                                const label = meta?.label ?? k;
                                const unit = meta?.unit;
                                const displayVal = typeof v === 'boolean' ? (v ? 'Sí' : 'No') : String(v ?? '');
                                return (
                                  <div key={k} className={group === "Certificaciones" ? "rounded-md bg-muted/40 p-2" : "flex gap-1"}>
                                    <span className="text-muted-foreground shrink-0">{label}:</span>
                                    <span className={group === "Certificaciones" ? "mt-1 block max-h-24 overflow-y-auto break-words font-medium leading-relaxed" : "break-words font-medium"}>
                                      {displayVal}{unit && displayVal !== '' ? ` ${unit}` : ''}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </details>
                )}
                {plan.proposedTechnicalSpecs && plan.proposedTechnicalSpecs.length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-muted-foreground">Datos originales de Wattwin ({plan.proposedTechnicalSpecs.length})</summary>
                    <div className="mt-2 grid gap-1 sm:grid-cols-2">
                      {plan.proposedTechnicalSpecs.slice(0, 20).map((s: any) => (
                        <div key={s.key} className="flex gap-1">
                          <span className="text-muted-foreground">{s.key}:</span>
                          <span className="break-words">{String(s.value)}{s.unit ? ` ${s.unit}` : ''}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                {plan.proposedAssetsPlan && (
                  <div className="text-xs text-muted-foreground">
                    Assets: imagen {plan.proposedAssetsPlan.hasMainImage ? 'sí' : 'no'} · docs {plan.proposedAssetsPlan.documentCount} · fichas {plan.proposedAssetsPlan.technicalSheetCount}
                    {plan.proposedAssetsPlan.pendingDownload?.length > 0 && ` · Pendiente descarga: ${plan.proposedAssetsPlan.pendingDownload.join(', ')}`}
                  </div>
                )}
                {plan.warnings?.length > 0 && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                    {plan.warnings.join(' · ')}
                  </div>
                )}
                {plan.reasons?.length > 0 && (
                  <div className="text-xs text-muted-foreground">{plan.reasons.join(' · ')}</div>
                )}
              </div>
            ))}
            {/* Import real result / error */}
            {wattwinImportRealResult?.result === 'created' && (
              <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900 space-y-2">
                <div className="font-medium">Producto importado correctamente en staging</div>
                <div className="text-xs">ID: {wattwinImportRealResult.product?.id} · SKU: {wattwinImportRealResult.product?.sku}</div>
                <div className="text-xs text-muted-foreground">Assets pendientes de descargar en fase posterior (MVP2-C).</div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-1"
                  onClick={() => {
                    setSearch(wattwinImportRealResult.product?.sku ?? '');
                    setActive('true');
                    setWattwinImportDryRunOpen(false);
                    setWattwinPreviewOpen(false);
                  }}
                >
                  Ver en catálogo (activos)
                </Button>
              </div>
            )}
            {wattwinImportRealResult?.result === 'already_imported' && (
              <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 space-y-2">
                <div>Este producto ya estaba importado (ref ID: {wattwinImportRealResult.existingRefId}).</div>
                {canRunWattwinAdmin && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const ids = Array.from(wattwinSelectedIds);
                          if (ids.length !== 1) return;
                          const result = await wattwinActivateMutation.mutateAsync({
                            wattwinProductId: ids[0],
                            confirm: 'ACTIVATE_WATTWIN_IMPORTED_PRODUCT_STAGING_ONLY',
                          });
                          setWattwinImportRealResult({ ...result, result: 'activated' });
                          await invalidateCatalog();
                          toast.success(`Producto activado: ${result.sku}`);
                        } catch (e: any) {
                          toast.error('No se pudo activar: ' + (e?.message ?? 'error desconocido'));
                        }
                      }}
                      disabled={wattwinActivateMutation.isPending}
                    >
                      {wattwinActivateMutation.isPending ? 'Activando…' : 'Activar producto en catálogo'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const ids = Array.from(wattwinSelectedIds);
                          if (ids.length !== 1) return;
                          const result = await wattwinRemapSpecsMutation.mutateAsync({
                            wattwinProductId: ids[0],
                            confirm: 'REMAP_CANONICAL_SPECS_STAGING_ONLY',
                          });
                          setWattwinImportRealResult({ ...result });
                          toast.success(`Specs canónicas remapeadas: ${result.canonicalFieldCount} campos (SKU: ${result.sku})`);
                        } catch (e: any) {
                          toast.error('No se pudo remapear specs: ' + (e?.message ?? 'error desconocido'));
                        }
                      }}
                      disabled={wattwinRemapSpecsMutation.isPending}
                    >
                      {wattwinRemapSpecsMutation.isPending ? 'Remapeando…' : 'Remapear specs canónicas'}
                    </Button>
                  </div>
                )}
              </div>
            )}
            {wattwinImportRealResult?.result === 'remapped' && (
              <div className="rounded-md border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900 space-y-2">
                <div className="font-medium">Specs canónicas actualizadas correctamente</div>
                <div className="text-xs">Producto ID: {wattwinImportRealResult.productId} · SKU: {wattwinImportRealResult.sku} · {wattwinImportRealResult.canonicalFieldCount} campos mapeados</div>
                <div className="text-xs font-mono break-all">{JSON.stringify(wattwinImportRealResult.canonicalFields, null, 0)}</div>
              </div>
            )}
            {wattwinImportRealResult?.result === 'activated' && (
              <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900 space-y-2">
                <div className="font-medium">Producto activado correctamente</div>
                <div className="text-xs">ID: {wattwinImportRealResult.productId} · SKU: {wattwinImportRealResult.sku}</div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearch(wattwinImportRealResult.sku ?? '');
                    setActive('true');
                    setWattwinImportDryRunOpen(false);
                    setWattwinPreviewOpen(false);
                  }}
                >
                  Ver en catálogo (activos)
                </Button>
              </div>
            )}
            {wattwinImportRealResult?.result === 'batch_created' && (
              <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900 space-y-2">
                <div className="font-medium">Productos importados correctamente en staging</div>
                <div className="text-xs">Total: {wattwinImportRealResult.count}</div>
                <div className="text-xs text-muted-foreground">Assets no descargados por este flujo.</div>
              </div>
            )}
            {wattwinImportRealError && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
                {wattwinImportRealError}
              </div>
            )}
          </div>
          {/* Sticky footer — always visible without scrolling */}
          <DialogFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => setWattwinImportDryRunOpen(false)}>Cerrar</Button>
            {(() => {
              const importFrozen = wattwinImportDryRunResult?.importFrozen === true;
              if (importFrozen) {
                return (
                  <div className="flex flex-col items-stretch gap-1 sm:items-end">
                    <Button disabled className="opacity-60" title="Importación pausada en backend.">
                      Importación pausada
                    </Button>
                    <p className="text-xs text-muted-foreground">El backend mantiene cerrado el import real.</p>
                  </div>
                );
              }
              const plans = wattwinImportDryRunResult?.plans ?? [];
              const importMode = wattwinImportDryRunResult?.importMode ?? 'closed';
              const importMaxBatch = Number(wattwinImportDryRunResult?.importMaxBatch ?? 1);
              const validPlans = plans.filter((plan: any) => plan?.action === 'create_product' && plan?.gateResult?.ok !== false);
              const gateOk = plans.every((plan: any) => plan?.gateResult?.ok !== false);
              const canImport = canRunWattwinAdmin
                && plans.length > 0
                && plans.length === wattwinSelectedCount
                && validPlans.length === plans.length
                && wattwinSelectedCount <= importMaxBatch
                && !wattwinImportDryRunLoading
                && !wattwinImportRealLoading
                && wattwinImportRealResult?.result !== 'created'
                && wattwinImportRealResult?.result !== 'batch_created';
              if (!canRunWattwinAdmin || plans.length === 0) {
                return <Button disabled title="La importación real requiere un plan válido">Importación pausada</Button>;
              }
              const importLabel = importMode === 'allowlist'
                ? 'Importación limitada a productos autorizados'
                : importMode === 'safe'
                  ? 'Importar productos válidos'
                  : 'Importación pausada';
              return (
                <Button
                  onClick={() => setWattwinImportRealConfirmOpen(true)}
                  disabled={!canImport}
                  title={!gateOk ? 'Importación bloqueada: faltan datos técnicos mínimos' : wattwinSelectedCount > importMaxBatch ? 'Selección superior al límite permitido' : undefined}
                >
                  {wattwinImportRealLoading ? 'Importando…' : `${importLabel}${wattwinSelectedCount > 0 ? ` (${wattwinSelectedCount})` : ''}`}
                </Button>
              );
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MVP2-B import confirm dialog */}
      <Dialog open={wattwinImportRealConfirmOpen} onOpenChange={setWattwinImportRealConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar importación en staging</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>Vas a importar la selección autorizada en staging. No se descargan imágenes ni fichas todavía. ¿Continuar?</p>
            <div className="rounded-md border bg-muted/40 p-3 text-xs space-y-1">
              <div>Productos seleccionados: <strong>{wattwinSelectedCount}</strong></div>
              <div>Primer producto: <strong>{wattwinImportDryRunResult?.plans?.[0]?.proposedProduct?.name ?? '—'}</strong></div>
              <div>Fabricante: {displayWattwinManufacturer(wattwinImportDryRunResult?.plans?.[0]?.proposedProduct?.manufacturer)}</div>
              <div>Familia: {wattwinImportDryRunResult?.plans?.[0]?.proposedProduct?.family ?? '—'}</div>
              <div className="text-muted-foreground">Estado inicial: inactivo (pendiente de revisión)</div>
              <div className="text-muted-foreground">Assets: pendientes de fase posterior</div>
            </div>
            {wattwinImportRealError && (
              <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-900 break-words">
                {wattwinImportRealError}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWattwinImportRealConfirmOpen(false)} disabled={wattwinImportRealLoading}>Cancelar</Button>
            <Button onClick={runWattwinImportReal} disabled={wattwinImportRealLoading}>
              {wattwinImportRealLoading ? 'Importando…' : 'Confirmar importación'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        setDeleteDialogOpen(open);
        if (!open) {
          setDeleteTargetProduct(null);
          setDeleteConfirmText("");
        }
      }}>
        <DialogContent className="flex max-h-[90dvh] max-w-lg flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>Eliminar producto del catálogo</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            {deleteTargetProduct && (
              <div className="space-y-4 py-1">
                <div className="rounded border border-destructive/30 bg-destructive/5 p-3 text-sm">
                  Vas a eliminar este producto del catálogo. Esta acción no debe hacerse si el producto ya se usa en presupuestos, BOM, compras o materiales. ¿Seguro que quieres continuar?
                </div>
                <div className="grid gap-2 rounded border p-3 text-sm sm:grid-cols-2">
                  <div><span className="text-muted-foreground">SKU</span><div className="font-medium">{deleteTargetProduct.sku}</div></div>
                  <div><span className="text-muted-foreground">Modelo</span><div>{deleteTargetProduct.model || "—"}</div></div>
                  <div className="sm:col-span-2"><span className="text-muted-foreground">Nombre</span><div>{deleteTargetProduct.name}</div></div>
                  <div><span className="text-muted-foreground">Familia</span><div>{familyLabel(deleteTargetProduct.family)}</div></div>
                  <div><span className="text-muted-foreground">Origen</span><div>{deleteTargetProduct.externalRef ? "Wattwin" : "Manual / sin ref externa"}</div></div>
                </div>
                {deletePreviewQuery.isLoading && <div className="text-sm text-muted-foreground">Comprobando dependencias…</div>}
                {deletePreviewQuery.error && (
                  <div className="rounded border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                    {deletePreviewQuery.error.message}
                  </div>
                )}
                {deletePreviewQuery.data && (
                  <div className="space-y-2 text-sm">
                    {deletePreviewQuery.data.dependencies.operational.length > 0 ? (
                      <div className="rounded border border-destructive/30 bg-destructive/5 p-3">
                        <div className="font-medium text-destructive">Dependencias operativas encontradas</div>
                        <ul className="mt-2 list-disc pl-5">
                          {deletePreviewQuery.data.dependencies.operational.map((dep: any) => (
                            <li key={dep.table}>{dep.table}: {dep.count} · {dep.reason}</li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div className="rounded border p-3 text-muted-foreground">Sin dependencias operativas detectadas.</div>
                    )}
                    {deletePreviewQuery.data.dependencies.catalogOwned.length > 0 && (
                      <div className="rounded border p-3">
                        <div className="font-medium">Filas técnicas propias que se eliminarán</div>
                        <ul className="mt-2 list-disc pl-5 text-muted-foreground">
                          {deletePreviewQuery.data.dependencies.catalogOwned.map((dep: any) => (
                            <li key={dep.table}>{dep.table}: {dep.count}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                {deletePreviewQuery.data?.canDelete === false ? (
                  <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    Este producto ya está asociado a otros registros. No se puede eliminar sin perder trazabilidad. Puedes descatalogarlo para que no vuelva a usarse.
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Escribe DELETE_PRODUCT_{deleteTargetProduct.id}</Label>
                    <Input value={deleteConfirmText} onChange={(event) => setDeleteConfirmText(event.target.value)} />
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 border-t pt-3">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
            {deletePreviewQuery.data?.canDelete !== false && (
              <Button
                variant="destructive"
                disabled={
                  !deleteTargetProduct ||
                  deleteConfirmText !== `DELETE_PRODUCT_${deleteTargetProduct.id}` ||
                  deletePreviewQuery.isLoading ||
                  !!deletePreviewQuery.error ||
                  deleteProduct.isPending
                }
                onClick={() => {
                  if (!deleteTargetProduct) return;
                  deleteProduct.mutate({ id: deleteTargetProduct.id, confirm: deleteConfirmText });
                }}
              >
                {deleteProduct.isPending ? "Eliminando…" : "Eliminar"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form.id ? "Editar producto" : "Nuevo producto"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 md:grid-cols-2">
            <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
            <div><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Familia</Label><Select value={form.family} onValueChange={(value: any) => setForm({ ...form, family: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{FAMILIES.map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Unidad</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
            <div><Label>Subcategoría / tipo interno</Label><Input value={categoryValueForEditDisplay(form.category)} placeholder={isRawCategoryIdentifier(form.category) ? "Sin subcategoría legible" : (CATEGORY_PLACEHOLDERS[form.family] || "Opcional")} onChange={(e) => setForm({ ...form, category: e.target.value })} />{isRawCategoryIdentifier(form.category) ? <p className="mt-1 text-xs text-muted-foreground">Sin subcategoría legible; se conserva el valor interno al guardar si no lo editas.</p> : <p className="mt-1 text-xs text-muted-foreground">Opcional. Sirve para ordenar el catálogo; la familia ya clasifica el producto.</p>}</div>
            <div><Label>Marca</Label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></div>
            <div><Label>Modelo</Label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} /></div>
            <div><Label>Código barras</Label><Input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} /></div>
            <div><Label>Proveedor principal</Label><Select value={form.defaultSupplierId} onValueChange={(value) => setForm({ ...form, defaultSupplierId: value })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Sin proveedor</SelectItem>{suppliers.map((supplier: any) => <SelectItem key={supplier.id} value={String(supplier.id)}>{supplier.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Precio compra actual</Label><Input inputMode="decimal" value={form.purchasePriceCurrent} onChange={(e) => setForm(applyPurchasePriceChange(form, e.target.value, lastPriceEdit))} /></div>
            <div><Label>Precio venta recomendado</Label><Input inputMode="decimal" value={form.salePriceRecommended} onChange={(e) => { setLastPriceEdit("sale"); setForm(applySalePriceChange(form, e.target.value)); }} /></div>
            <div><Label>Margen previsto (%)</Label><Input inputMode="decimal" value={marginPct ?? ""} onChange={(e) => { setLastPriceEdit("margin"); setForm(applyMarginPctChange(form, e.target.value)); }} /><p className="mt-1 text-xs text-muted-foreground">Margen sobre venta: ((venta - compra) / venta) × 100. Debe ser ≥ 0 y &lt; 100.</p></div>
            <div className="flex items-center gap-3 pt-6"><Switch checked={form.discontinued} onCheckedChange={(checked) => setForm({ ...form, discontinued: checked })} /><Label>Descatalogado</Label></div>
            <div className="md:col-span-2 rounded border bg-amber-50 p-3 text-sm text-amber-900">Puedes guardar el producto incompleto, pero para activarlo deberán completarse los campos obligatorios.</div>
            <div className="md:col-span-2"><Label>Descripción</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="md:col-span-2"><Label>Descripción técnica</Label><Textarea value={form.technicalDescription} onChange={(e) => setForm({ ...form, technicalDescription: e.target.value })} /></div>
            <div className="md:col-span-2"><Label>Notas</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div className="md:col-span-2 space-y-4 rounded border p-3">
              <div><h3 className="font-semibold">Ficha técnica por familia</h3><p className="text-sm text-muted-foreground">Los campos marcados con * son obligatorios para activar. Entrada manual en unidades canónicas ODS: W/Wp/Wh/V/A/mm/kg/%/años.</p></div>
              {Object.entries(groupedSpecFields(form.family)).map(([group, fields]) => (
                <div key={group} className="space-y-2">
                  <h4 className="text-sm font-medium">{group}</h4>
                  <div className="grid gap-3 md:grid-cols-2">
                    {fields.map((field) => {
                      const fieldValue = form.technicalSpecs[field.key];
                      const hasFieldValue = hasValue(fieldValue);
                      const fieldHelp = hasFieldValue && field.help === "Pendiente de completar" ? undefined : field.help;
                      const fieldPlaceholder = !hasFieldValue && field.help === "Pendiente de completar" ? "Pendiente de completar" : fieldHelp || (field.unit ? `Valor en ${field.unit}` : undefined);
                      return (
                      <div key={field.key}>
                        <Label>{field.label}{field.unit && !field.label.includes("(") ? ` (${field.unit})` : ""}{field.requiredForActive ? " *" : ""}</Label>
                        {field.type === "boolean" ? (
                          <Select value={form.technicalSpecs[field.key] === true ? "true" : form.technicalSpecs[field.key] === false ? "false" : ""} onValueChange={(value) => setForm({ ...form, technicalSpecs: { ...form.technicalSpecs, [field.key]: value === "true" } })}><SelectTrigger><SelectValue placeholder="Seleccionar Sí/No" /></SelectTrigger><SelectContent><SelectItem value="true">Sí</SelectItem><SelectItem value="false">No</SelectItem></SelectContent></Select>
                        ) : field.type === "select" ? (
                          <Select value={String(form.technicalSpecs[field.key] || "")} onValueChange={(value) => setForm({ ...form, technicalSpecs: { ...form.technicalSpecs, [field.key]: value } })}><SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger><SelectContent>{(field.options || []).map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}</SelectContent></Select>
                        ) : (
                          <>
                            <Input type="text" inputMode={field.type === "number" ? "decimal" : undefined} placeholder={fieldPlaceholder} value={String(form.technicalSpecs[field.key] ?? "")} onChange={(e) => setForm({ ...form, technicalSpecs: { ...form.technicalSpecs, [field.key]: e.target.value } })} />
                            {fieldHelp && <p className="mt-1 text-xs text-muted-foreground">{fieldHelp}</p>}
                          </>
                        )}
                      </div>
                    );
                    })}
                  </div>
                </div>
              ))}
              {!specFieldsForFamily(form.family).length && <Textarea placeholder="Especificaciones genéricas" value={String(form.technicalSpecs.generic || "")} onChange={(e) => setForm({ ...form, technicalSpecs: { ...form.technicalSpecs, generic: e.target.value } })} />}
            </div>
            <div className="md:col-span-2 space-y-3 rounded border p-3">
              <h3 className="font-semibold">Documentos</h3>
              {form.id && (() => {
                const status = editAssetStatusForDisplay(editAssetsQuery.data ?? []);
                return (
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded border bg-muted/20 p-3 text-sm">
                      <div className="font-medium">Imagen actual</div>
                      <div className="text-muted-foreground">{editAssetsQuery.isLoading ? "Comprobando..." : status.image === "cached" ? "Cacheada" : "No disponible"}</div>
                    </div>
                    <div className="rounded border bg-muted/20 p-3 text-sm">
                      <div className="font-medium">Ficha técnica actual</div>
                      <div className="text-muted-foreground">{editAssetsQuery.isLoading ? "Comprobando..." : status.technicalSheet === "cached" ? "Cacheada" : "No disponible"}</div>
                      {status.technicalSheetUrls.length > 0 && <div className="mt-2 flex flex-wrap gap-2">{status.technicalSheetUrls.map((url) => <a key={url} className="inline-flex items-center rounded border px-3 py-2 text-ods-navigation underline" href={url} target="_blank" rel="noreferrer">Ver ficha técnica</a>)}</div>}
                    </div>
                  </div>
                );
              })()}
              <div className="grid gap-3 md:grid-cols-2">
                <div><Label>Reemplazar imagen destacada JPG/PNG/WEBP</Label><Input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => { setFeaturedImageFile(e.target.files?.[0] || null); setProductFormError(null); }} />{featuredImageFile && <p className="mt-1 text-xs text-muted-foreground">Seleccionada: {featuredImageFile.name}</p>}</div>
                <div><Label>Reemplazar ficha técnica PDF</Label><Input type="file" accept="application/pdf" onChange={(e) => { setTechnicalSheetFile(e.target.files?.[0] || null); setProductFormError(null); }} />{technicalSheetFile && <p className="mt-1 text-xs text-muted-foreground">Seleccionada: {technicalSheetFile.name}</p>}</div>
              </div>
              <p className="text-xs text-muted-foreground"><Upload className="mr-1 inline h-3 w-3" />Se sube a Spaces/product_assets al guardar; no se guarda binario en PostgreSQL.</p>
            </div>
            {productFormError && <div className="md:col-span-2 rounded border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{productFormError}</div>}
          </div>
          <DialogFooter className="gap-2"><Button variant="outline" onClick={() => setProductDialogOpen(false)}>Cancelar</Button><Button variant="secondary" disabled={!canSaveDraftOrChanges || createProduct.isPending || updateProduct.isPending || uploadManualAsset.isPending} onClick={() => saveProductForm(false)}>{form.id ? "Guardar cambios" : "Guardar borrador"}</Button><Button disabled={!canSaveDraftOrChanges || createProduct.isPending || updateProduct.isPending || uploadManualAsset.isPending} onClick={() => saveProductForm(true)}>Guardar y activar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-[96vw] md:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{detailProduct?.name || "Detalle del producto"}</DialogTitle></DialogHeader>
          {detailProduct && (
            <div className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div><Label>Familia</Label><div className="text-sm">{familyLabel(detailProduct.family)}</div></div>
                <div><Label>Marca / modelo</Label><div className="text-sm">{[detailProduct.brand, detailProduct.model].filter(Boolean).join(" · ") || "—"}</div></div>
                <div><Label>SKU</Label><div className="text-sm">{detailProduct.sku}</div></div>
                <div><Label>Origen / estado</Label><div className="text-sm flex gap-2">{detailProduct.externalRef ? <Badge variant="outline">Wattwin</Badge> : <Badge variant="secondary">Manual</Badge>}<Badge variant={detailProduct.active ? "default" : "secondary"}>{detailProduct.active ? "Activo" : "Inactivo"}</Badge></div></div>
              </div>
              <div className="rounded border p-3">
                <h3 className="font-semibold">Imagen destacada</h3>
                {(() => {
                  const assets = detailAssetsQuery.data ?? [];
                  const featured = getProductPrimaryImage(assets);
                  const vendorNotApiAsset = !featured && assets.find((a: any) =>
                    a.purpose === "main_image" && ["vendor_content_resource", "vendor_path_forbidden"].includes(a.status)
                  );
                  return featured ? (
                    <>
                      <div className="mt-2 flex h-56 w-full items-center justify-center overflow-hidden rounded border bg-muted/20 md:h-72">
                        <img className="h-full w-full object-contain object-center" src={featured.viewUrl} alt={detailProduct.name} />
                      </div>
                      {featured.purpose === "gallery_image" && (
                        <p className="mt-1 text-xs text-muted-foreground italic">Imagen de galería usada como destacada</p>
                      )}
                    </>
                  ) : vendorNotApiAsset ? (
                    <div className="mt-2 rounded border bg-muted/30 p-6 text-center text-sm text-muted-foreground">Imagen del fabricante no descargable por API</div>
                  ) : <div className="mt-2 rounded border bg-muted/30 p-6 text-center text-sm text-muted-foreground">Sin imagen cacheada</div>;
                })()}
              </div>
              <div className="rounded border p-3">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="font-semibold">Precios de catálogo</h3>
                  <Badge variant="outline">Fuente: {detailProduct.externalRef ? "Wattwin" : "Manual"}</Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Precio/margen previsto de catálogo; no margen real de obra.</p>
                <div className="mt-3 grid gap-2 sm:grid-cols-3">
                  <div className="rounded bg-muted/40 p-2"><Label>Precio compra</Label><div className="text-sm">{formatCatalogPrice(detailProduct.purchasePriceCurrent)}</div></div>
                  <div className="rounded bg-muted/40 p-2"><Label>Precio venta recomendado</Label><div className="text-sm">{formatCatalogPrice(detailProduct.salePriceRecommended)}</div></div>
                  <div className="rounded bg-muted/40 p-2"><Label>Margen esperado</Label><div className="text-sm">{expectedMargin(detailProduct.purchasePriceCurrent, detailProduct.salePriceRecommended)}</div></div>
                </div>
                {!hasCatalogPricing(detailProduct) && <p className="mt-2 text-xs text-muted-foreground">Sin precios de catálogo registrados todavía.</p>}
              </div>
              <div>
                <h3 className="font-semibold">Especificaciones técnicas</h3>
                {technicalSpecsQuery.isLoading ? <p className="text-sm text-muted-foreground">Cargando…</p> : (technicalSpecsQuery.data ?? []).length ? (() => {
                  const { primary, legacy } = operationalTechnicalSpecsRows(technicalSpecsQuery.data ?? []);
                  return primary ? (
                    <div className="mt-2 space-y-3">
                      <div className="rounded border p-3">
                        <div className="mb-2 flex items-center justify-between gap-2"><Badge variant="secondary">{primary.schemaVersion}</Badge><span className="text-xs text-muted-foreground">Fuente operativa: {primary.source === "wattwin" ? "Wattwin canonical" : primary.source}</span></div>
                        <div className="space-y-4">
                          {normalizedDisplaySpecs(primary.family, primary.specs || {}, detailProduct).map((group) => (
                            <div key={group.title}>
                              <h4 className="mb-2 text-sm font-semibold">{group.title}</h4>
                              <div className="grid gap-2 sm:grid-cols-2">
                                {group.items.map((entry) => (
                                  <div key={`${group.title}-${entry.label}`} className="rounded bg-muted/40 p-2">
                                    <Label>{entry.label}</Label>
                                    <div className="text-sm break-words">{formatSpecValue(entry.value, entry.format)}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      {!!legacy.length && (
                        <details className="rounded border bg-muted/20 p-3 text-xs text-muted-foreground">
                          <summary className="cursor-pointer font-medium text-foreground">Datos originales Wattwin / legacy</summary>
                          <div className="mt-3 space-y-3">
                            {legacy.map((spec: any) => (
                              <div key={spec.id} className="rounded border bg-background p-3">
                                <div className="mb-2 flex items-center justify-between gap-2"><Badge variant="outline">{spec.schemaVersion}</Badge><span>Origen: {spec.source === "wattwin" ? "Wattwin histórico" : spec.source}</span></div>
                                <div className="grid gap-2 sm:grid-cols-2">
                                  {rawDisplayEntries(spec.specs || {}).slice(0, 30).map(([key, value]) => (
                                    <div key={key} className="rounded bg-muted/40 p-2"><Label>{key}</Label><div className="break-words">{asText(value)}</div></div>
                                  ))}
                                </div>
                                {rawFieldCount(spec.specs || {}) > 0 && <div className="mt-2">rawProperties: {rawFieldCount(spec.specs || {})} campos originales</div>}
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  ) : <p className="text-sm text-muted-foreground">Sin especificaciones técnicas registradas.</p>;
                })() : <p className="text-sm text-muted-foreground">Sin especificaciones técnicas registradas.</p>}
              </div>
              <div>
                <h3 className="font-semibold">Documentos e imágenes</h3>
                {detailAssetsQuery.isLoading ? <p className="text-sm text-muted-foreground">Cargando…</p> : (() => {
                  const assets = detailAssetsQuery.data ?? [];
                  const gallery = cachedAssetsByPurpose(assets, "gallery_image").filter((asset: any) => asset.id !== getProductPrimaryImage(assets)?.id);
                  const documentPurposes = ["technical_sheet", "catalog", "certificate", "manual", "other"];
                  return (
                    <div className="mt-2 space-y-3">
                      {gallery.length > 0 && (
                        <div className="rounded border p-3">
                          <h4 className="text-sm font-semibold">Galería</h4>
                          <div className="mt-2 grid gap-2 sm:grid-cols-3">
                            {gallery.map((asset: any) => (
                              <img key={asset.id} className="h-28 w-full rounded border object-contain" src={asset.viewUrl} alt={assetDisplayName(asset, detailProduct.name)} />
                            ))}
                          </div>
                        </div>
                      )}
                      {documentPurposes.map((purpose) => {
                        const docs = cachedDocumentAssetsByPurpose(assets, purpose);
                        if (!docs.length && purpose !== "technical_sheet") return null;
                        if (!docs.length && purpose === "technical_sheet") {
                          return <div key={purpose} className="rounded border p-3 text-sm"><div className="font-medium">Ficha técnica</div><div className="text-muted-foreground">No disponible todavía</div></div>;
                        }
                        return (
                          <div key={purpose} className="rounded border p-3 text-sm">
                            <div className="font-medium">{ASSET_PURPOSE_LABELS[purpose] || purpose}</div>
                            <div className="mt-2 flex flex-wrap gap-2">
                              {docs.map((asset: any) => (
                                <a key={asset.id} className="inline-flex items-center rounded border px-3 py-2 text-ods-navigation underline" href={asset.viewUrl} target="_blank" rel="noreferrer">
                                  {documentButtonLabel(purpose, docs.length)}
                                  {docs.length > 1 ? ` · ${assetDisplayName(asset)}` : ""}
                                </a>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                      {(() => {
                        const primaryImageAsset = getProductPrimaryImage(assets);
                        const hasMainImageCached = cachedAssetsByPurpose(assets, "main_image").length > 0;
                        const hasGalleryImageForGallery = cachedAssetsByPurpose(assets, "gallery_image").filter((a: any) => a.id !== primaryImageAsset?.id).length > 0;
                        const hasTechnicalSheetCached = cachedDocumentAssetsByPurpose(assets, "technical_sheet").length > 0;
                        const hasTechnicalSheetMissingViewUrl = !hasTechnicalSheetCached && assets.some((a: any) => a.purpose === "technical_sheet" && a.status === "cached" && !a.viewUrl);
                        const isTechSheetByOther = !hasTechnicalSheetCached && !hasTechnicalSheetMissingViewUrl && cachedDocumentAssetsByPurpose(assets, "other").length > 0;
                        const hasOtherDocsForDisplay = ["catalog", "certificate", "manual"].some(p => cachedDocumentAssetsByPurpose(assets, p).length > 0);
                        return (
                          <div className="space-y-1 text-sm text-muted-foreground">
                            <div>
                              <span className="font-medium">Imagen:</span>{' '}
                              {!primaryImageAsset ? (
                                assets.some((a: any) => a.purpose === "main_image" && ["vendor_content_resource", "vendor_path_forbidden"].includes(a.status))
                                  ? "Imagen del fabricante no descargable por API"
                                  : "Sin imagen cacheada"
                              ) :
                                primaryImageAsset.purpose === "main_image" ? "Imagen principal cacheada" :
                                primaryImageAsset.purpose === "gallery_image" ? "Imagen de galería usada como destacada" :
                                "Imagen cacheada"}
                            </div>
                            <div>
                              <span className="font-medium">Ficha técnica:</span>{' '}
                              {hasTechnicalSheetCached ? "Ficha técnica PDF cacheada" :
                                hasTechnicalSheetMissingViewUrl ? "Ficha técnica cacheada sin URL de vista" :
                                isTechSheetByOther ? "Ficha técnica por PDF genérico" :
                                "Ficha técnica no disponible"}
                            </div>
                            {hasGalleryImageForGallery && <div className="text-muted-foreground">Galería: {gallery.length} imagen(es) adicional(es)</div>}
                            {hasOtherDocsForDisplay && <div className="text-muted-foreground">Documentos adicionales (catálogo, certificado, manual) disponibles</div>}
                          </div>
                        );
                      })()}
                      {canManage && assets.length > 0 && (
                        <details className="rounded border bg-muted/20 p-3 text-xs text-muted-foreground">
                          <summary className="cursor-pointer font-medium text-foreground">Diagnóstico técnico de assets</summary>
                          {(() => {
                            const byPurpose = assets.reduce((acc: Record<string, any[]>, asset: any) => {
                              const purpose = asset.purpose || "unknown";
                              if (!acc[purpose]) acc[purpose] = [];
                              acc[purpose].push(asset);
                              return acc;
                            }, {} as Record<string, any[]>);
                            return (
                              <div className="mt-2">
                                <div className="mb-3 grid gap-2 sm:grid-cols-2">
                                  {Object.entries(byPurpose).map(([purpose, items]) => {
                                    const purposeItems = items as any[];
                                    return (
                                    <div key={purpose} className="rounded border bg-background p-2">
                                      <div className="font-medium text-foreground">{ASSET_PURPOSE_LABELS[purpose] || purpose}</div>
                                      <div className="mt-1 space-y-1">
                                        <div>Total: {purposeItems.length}</div>
                                        <div>En caché: {purposeItems.filter((a: any) => a.status === "cached").length}</div>
                                        <div>Pendientes: {purposeItems.filter((a: any) => a.status !== "cached").length}</div>
                                        {purposeItems.filter((a: any) => a.reason).length > 0 && (
                                          <details className="mt-1">
                                            <summary className="cursor-pointer text-muted-foreground">Motivos técnicos ({purposeItems.filter((a: any) => a.reason).length})</summary>
                                            <div className="mt-1 space-y-1">
                                              {purposeItems.filter((a: any) => a.reason).map((a: any) => (
                                                <div key={a.id} className="pl-2 border-l-2 border-muted-foreground/30">
                                                  <div>{a.title || a.fileName || a.purpose}</div>
                                                  <div className="text-foreground/70">{a.reason}</div>
                                                </div>
                                              ))}
                                            </div>
                                          </details>
                                        )}
                                      </div>
                                    </div>
                                    );
                                  })}
                                </div>
                                <div className="space-y-3">
                                  {assets.map((asset: any) => (
                                    <div key={asset.id} className="rounded border bg-background p-2">
                                      <div className="font-medium text-foreground">{ASSET_PURPOSE_LABELS[asset.purpose] || asset.purpose} · {assetStatusText(asset)}</div>
                                      <div>Tipo: {asset.assetType || "—"} · MIME: {asset.mimeType || "—"} · Vista app: {asset.viewUrl ? "sí" : "no"}</div>
                                      {asset.status === "cached" && !asset.viewUrl && <div className="text-destructive">Asset cacheado sin URL de vista app-local</div>}
                                      <div>Campo: {asset.sourceFieldsSeen?.length ? asset.sourceFieldsSeen.join(", ") : "—"}</div>
                                      {asset.reason && <div>Motivo técnico: {asset.reason}</div>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                        </details>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
          {detailProduct && (
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const productToEdit = detailProduct;
                  setDetailDialogOpen(false);
                  window.setTimeout(() => openEditProduct(productToEdit), 0);
                }}
              >
                <Pencil className="mr-2 h-4 w-4" />Editar producto
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <SupplierCreateModal context="catalog" open={supplierDialogOpen} onOpenChange={setSupplierDialogOpen} onCreated={() => { void invalidateCatalog(); }} />

      <Dialog open={aliasDialogOpen} onOpenChange={setAliasDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Alias del producto</DialogTitle></DialogHeader>
          <div className="flex gap-2"><Input placeholder="Alias" value={alias} onChange={(e) => setAlias(e.target.value)} /><Button onClick={() => selectedProductId && addAlias.mutate({ productId: selectedProductId, alias })}>Añadir</Button></div>
          <div className="space-y-2">{(aliasesQuery.data ?? []).map((item: any) => <div key={item.id} className="flex items-center justify-between rounded border p-2"><span>{item.alias}</span><Button size="sm" variant="ghost" onClick={() => deleteAlias.mutate({ id: item.id })}>Eliminar</Button></div>)}</div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
